import axios, { AxiosInstance, AxiosResponse } from 'axios';
import {
  ApiResponse,
  AuthResponse,
  LoginForm,
  RegisterForm,
  MarketerStatsSummary,
  UnifiedStaffLoginResponse,
  IdentifyResponse,
  UnifiedLoginResponse,
  UnifiedSession,
  AccountType,
  ClientDashboardData,
  ClientTrainerCardData,
  PublicAccount,
  SubscriptionPlanItem,
  PublicPlanItem,
  SubscriptionGrantLogItem,
  LogFileInfoResponse,
  LogFileContentResponse,
} from '../types';
import { apiCache, generateCacheKey } from '../utils/apiCache';
import {
  fallbackToSchoolAccount,
  getActiveAccountId,
  removeSavedAccount,
} from '../utils/accountSwitcher';
import { clearStoredRefCode, getStoredRefCode } from '../utils/refCode';
import { getApiBaseUrl } from '../capacitor/platform';

export type NotificationPrefs = {
  actorType: string;
  actorId: string;
  chatMessagesEnabled: boolean;
  changelogEnabled: boolean;
  pushMasterEnabled: boolean;
  athleteCreatedEnabled?: boolean;
  financeEnabled?: boolean;
  attendanceEnabled?: boolean;
  offersEnabled?: boolean;
  salaryEnabled?: boolean;
  paymentsEnabled?: boolean;
  trainingRemindersEnabled?: boolean;
  scheduleChangesEnabled?: boolean;
  taskRemindersEnabled?: boolean;
  scheduleMode: 'ALWAYS' | 'WINDOW';
  windowStartMinutes: number | null;
  windowEndMinutes: number | null;
  daysOfWeek: string | null;
  timezone: string;
};

/** Разбор Content-Disposition: предпочитаем filename*=UTF-8''… (кириллица). */
function parseContentDispositionFilename(
  disposition: string | undefined,
  fallback: string
): string {
  const header = String(disposition || '');
  const utf8Match = header.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1].trim());
    } catch {
      return utf8Match[1].trim();
    }
  }
  const plainMatch = header.match(/filename\s*=\s*"?([^";]+)"?/i);
  if (plainMatch?.[1]) return plainMatch[1].trim();
  return fallback;
}

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: getApiBaseUrl(),
      timeout: 10000,
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      (config) => {
        // Check for super admin token first (for admin dashboard routes)
        const superAdminToken = localStorage.getItem('superAdminToken');
        const testerToken = localStorage.getItem('testerToken');
        const platformStaffToken = localStorage.getItem('platformStaffToken');
        const promoCodeAdminToken = localStorage.getItem('promoCodeAdminToken');
        const marketerToken = localStorage.getItem('marketerToken');
        const clientToken = localStorage.getItem('clientToken');
        const regularToken = localStorage.getItem('token');
        
        // Не перезаписываем Authorization, если уже задан (проверка доступа слота и т.п.)
        const existingAuth = config.headers?.Authorization || config.headers?.authorization;
        const url = String(config.url || '');
        // CRUD новостей — только SA; не даём школьному/другому Bearer перебить токен.
        const forceSuperAdmin =
          Boolean(superAdminToken) &&
          (url.includes('/platform/changelog') || url.includes('/admin-dashboard/'));

        if (forceSuperAdmin) {
          config.headers.Authorization = `Bearer ${superAdminToken}`;
        } else if (!existingAuth) {
          if (superAdminToken) {
            config.headers.Authorization = `Bearer ${superAdminToken}`;
          } else if (testerToken) {
            config.headers.Authorization = `Bearer ${testerToken}`;
          } else if (platformStaffToken && (url.includes('/platform-staff/') || url.includes('/platform-staff/auth/'))) {
            config.headers.Authorization = `Bearer ${platformStaffToken}`;
          } else if (promoCodeAdminToken) {
            config.headers.Authorization = `Bearer ${promoCodeAdminToken}`;
          } else if (marketerToken) {
            config.headers.Authorization = `Bearer ${marketerToken}`;
          } else if (clientToken) {
            config.headers.Authorization = `Bearer ${clientToken}`;
          } else if (regularToken) {
            config.headers.Authorization = `Bearer ${regularToken}`;
          }
        }

        // CSRF double-submit при cookie-сессии без Bearer
        if (!config.headers.Authorization && typeof document !== 'undefined') {
          const match = document.cookie.match(/(?:^|;\s*)crm_csrf=([^;]+)/);
          if (match?.[1]) {
            config.headers['X-CSRF-Token'] = decodeURIComponent(match[1]);
          }
        }

        // FormData: убрать application/json из defaults, иначе multer не увидит файлы.
        if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
          const headers = config.headers as any;
          if (headers && typeof headers.delete === 'function') {
            headers.delete('Content-Type');
          } else if (headers) {
            delete headers['Content-Type'];
            delete headers['content-type'];
          }
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor to handle errors
    this.api.interceptors.response.use(
      (response: AxiosResponse) => {
        return response;
      },
      (error) => {
        // Ignore cancelled requests (they are not real errors)
        if (axios.isCancel(error) || error?.code === 'ERR_CANCELED' || error?.message === 'canceled') {
          return Promise.reject(error);
        }
        
        if (error.response?.status === 503 && error.response?.data?.code === 'MAINTENANCE') {
          if (!localStorage.getItem('superAdminToken')) {
            sessionStorage.setItem('maintenanceMode', '1');
            sessionStorage.removeItem('testingModeAccess');
            sessionStorage.removeItem('closedTestingMode');
            window.dispatchEvent(
              new CustomEvent('maintenance-mode', {
                detail: {
                  enabled: true,
                  mode: 'maintenance',
                  message: error.response?.data?.error || '',
                },
              })
            );
            const path = window.location.pathname;
            if (
              path !== '/auth' &&
              path !== '/maintenance' &&
              path !== '/terms' &&
              path !== '/privacy'
            ) {
              window.location.href = '/maintenance';
            }
          }
          return Promise.reject(error);
        }

        if (error.response?.status === 503 && error.response?.data?.code === 'TESTING_MODE') {
          if (!localStorage.getItem('superAdminToken')) {
            sessionStorage.setItem('testingMode', '1');
            sessionStorage.removeItem('closedTestingMode');
            sessionStorage.removeItem('testingModeAccess');
            window.dispatchEvent(
              new CustomEvent('maintenance-mode', {
                detail: {
                  enabled: true,
                  mode: 'testing',
                  message: error.response?.data?.error || '',
                },
              })
            );
            const path = window.location.pathname;
            if (
              path !== '/auth' &&
              path !== '/maintenance' &&
              path !== '/terms' &&
              path !== '/privacy'
            ) {
              window.location.href = '/maintenance';
            }
          }
          return Promise.reject(error);
        }

        if (error.response?.status === 503 && error.response?.data?.code === 'CLOSED_TESTING') {
          sessionStorage.setItem('closedTestingMode', '1');
          sessionStorage.setItem('testingMode', '1');
          sessionStorage.removeItem('testingModeAccess');
          sessionStorage.removeItem('maintenanceMode');
          window.dispatchEvent(
            new CustomEvent('maintenance-mode', {
              detail: {
                enabled: true,
                mode: 'closed_testing',
                message: error.response?.data?.error || '',
              },
            })
          );
          const path = window.location.pathname;
          if (path !== '/maintenance' && path !== '/terms' && path !== '/privacy') {
            window.location.href = '/maintenance';
          }
          return Promise.reject(error);
        }

        if (error.response?.status === 401) {
          // Token expired or invalid
          const url = error.config?.url || '';
          
          // Don't redirect on login endpoints - let them handle the error
          const isLoginEndpoint = url.includes('/auth/login') || 
                                 url.includes('/auth/unified-staff-login') ||
                                 url.includes('/auth/identify') ||
                                 url.includes('/auth/unified-login') ||
                                 url.includes('/auth/setup-password') ||
                                 url.includes('/auth/select-account') ||
                                 url.includes('/auth/marketer/login') ||
                                 url.includes('/auth/promo-code-admin/login') ||
                                 url.includes('/auth/super-admin/login') ||
                                 url.includes('/platform-staff/auth/login') ||
                                 url.includes('/client-auth/login') ||
                                 url.includes('/client-auth/register');
          
          // Если это endpoint авторизации - не перенаправляем, просто пробрасываем ошибку
          if (isLoginEndpoint) {
            return Promise.reject(error);
          }

          const purgeActiveSlot = () => {
            const activeId = getActiveAccountId();
            if (activeId) removeSavedAccount(activeId);
          };
          
          // Check if it's a super admin route
          const isSuperAdminRoute = url.includes('/super-admin/') || 
                                    url.includes('/admin-dashboard') ||
                                    localStorage.getItem('superAdminToken');

          const isPlatformRoute =
            url.includes('/platform/') || localStorage.getItem('testerToken');
          
          // Check if it's a marketer route (marketers/me/stats, or any /marketers route with marketer token)
          const isMarketerRoute = url.includes('/marketers/me/') || 
                                  (url.includes('/marketers/') && localStorage.getItem('marketerToken'));
          
          // Check if it's a promo code admin route
          const isPromoCodeAdminRoute = url.includes('/promo-codes') || 
                                        url.includes('/referral-links') ||
                                        (url.includes('/marketers/') && !localStorage.getItem('marketerToken') && localStorage.getItem('promoCodeAdminToken'));
          
          if (isSuperAdminRoute && localStorage.getItem('superAdminToken')) {
            purgeActiveSlot();
            localStorage.removeItem('superAdminToken');
            localStorage.removeItem('superAdmin');
            const schoolDest = fallbackToSchoolAccount();
            window.location.href = schoolDest || '/auth';
          } else if (isPlatformRoute && localStorage.getItem('testerToken') && !localStorage.getItem('superAdminToken')) {
            purgeActiveSlot();
            localStorage.removeItem('testerToken');
            localStorage.removeItem('tester');
            const schoolDest = fallbackToSchoolAccount();
            window.location.href = schoolDest || '/auth';
          } else if (isMarketerRoute || (localStorage.getItem('marketerToken') && (url.includes('/promo-codes') || url.includes('/referral-links') || url.includes('/marketers')))) {
            purgeActiveSlot();
            localStorage.removeItem('marketerToken');
            localStorage.removeItem('marketer');
            localStorage.removeItem('marketerTenant');
            window.location.href = '/auth';
          } else if (isPromoCodeAdminRoute) {
            purgeActiveSlot();
            localStorage.removeItem('promoCodeAdminToken');
            localStorage.removeItem('promoCodeAdmin');
            localStorage.removeItem('promoCodeAdminTenant');
            window.location.href = '/auth';
          } else if (url.includes('/platform-staff/')) {
            purgeActiveSlot();
            localStorage.removeItem('platformStaffToken');
            localStorage.removeItem('platformStaff');
            window.location.href = '/auth';
          } else if (url.includes('/client-auth/')) {
            purgeActiveSlot();
            localStorage.removeItem('clientToken');
            localStorage.removeItem('client');
            localStorage.removeItem('clientTenant');
            localStorage.removeItem('userType');
            window.location.href = '/auth';
          } else if (localStorage.getItem('marketerToken') || localStorage.getItem('promoCodeAdminToken')) {
            // Школьный endpoint при активной сессии маркетолога/PCA — не сбрасывать в цикл /
            return Promise.reject(error);
          } else {
            purgeActiveSlot();
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            localStorage.removeItem('tenant');
            window.location.href = '/auth';
          }
        }
        return Promise.reject(error);
      }
    );
  }

  // Auth endpoints
  /* ---------------- Единая авторизация ---------------- */

  /** Шаг 1: проверяем телефон/email и узнаём, нужен ли экран создания пароля. */
  async identify(identifier: string): Promise<IdentifyResponse> {
    const response = await this.api.post<ApiResponse<IdentifyResponse>>('/auth/identify', {
      identifier,
    });
    return response.data.data!;
  }

  /** Шаг 2а: первый вход — создание пароля. */
  async setupPassword(payload: {
    identifier: string;
    password: string;
    confirmPassword: string;
    acceptTerms: boolean;
    acceptPrivacy: boolean;
    rememberMe?: boolean;
    accountType?: 'CLIENT' | 'PARENT';
    accountId?: string;
  }): Promise<UnifiedLoginResponse> {
    const response = await this.api.post<ApiResponse<UnifiedLoginResponse>>(
      '/auth/setup-password',
      payload
    );
    return response.data.data!;
  }

  /** Шаг 2б: вход по существующему паролю. */
  async unifiedLogin(payload: {
    identifier: string;
    password: string;
    rememberMe?: boolean;
  }): Promise<UnifiedLoginResponse> {
    const response = await this.api.post<ApiResponse<UnifiedLoginResponse>>(
      '/auth/unified-login',
      payload
    );
    return response.data.data!;
  }

  /** Шаг 3: выбор организации/роли, когда найдено несколько аккаунтов. */
  async selectAccount(payload: {
    selectionToken: string;
    accountType: AccountType;
    accountId: string;
  }): Promise<UnifiedSession> {
    const response = await this.api.post<ApiResponse<UnifiedSession>>(
      '/auth/select-account',
      payload
    );
    return response.data.data!;
  }

  async requestEmailLogin(payload: {
    identifier: string;
    rememberMe?: boolean;
  }): Promise<{ sent: boolean; challengeToken?: string; message?: string }> {
    const response = await this.api.post<ApiResponse>('/auth/login/email/request', payload);
    return response.data.data;
  }

  async verifyEmailLogin(payload: {
    challengeToken: string;
    code: string;
  }): Promise<UnifiedLoginResponse> {
    const response = await this.api.post<ApiResponse<UnifiedLoginResponse>>(
      '/auth/login/email/verify',
      payload
    );
    return response.data.data!;
  }

  async verifyEmail2fa(payload: {
    challengeToken: string;
    code: string;
  }): Promise<UnifiedLoginResponse> {
    const response = await this.api.post<ApiResponse<UnifiedLoginResponse>>(
      '/auth/login/2fa/verify',
      payload
    );
    return response.data.data!;
  }

  async requestPushLogin(payload: {
    identifier: string;
    rememberMe?: boolean;
  }): Promise<{ challengeToken: string; message?: string }> {
    const response = await this.api.post<ApiResponse>('/auth/login/push/request', payload);
    return response.data.data;
  }

  async verifyPushLogin(payload: {
    challengeToken: string;
    code: string;
  }): Promise<UnifiedLoginResponse> {
    const response = await this.api.post<ApiResponse<UnifiedLoginResponse>>(
      '/auth/login/push/verify',
      payload
    );
    return response.data.data!;
  }

  async startQrLogin(payload?: {
    rememberMe?: boolean;
  }): Promise<{ challengeToken: string; secret: string }> {
    const response = await this.api.post<ApiResponse>('/auth/login/qr/start', payload || {});
    return response.data.data;
  }

  async getQrLoginStatus(challengeToken: string): Promise<{
    status: string;
    session?: UnifiedLoginResponse;
  }> {
    const response = await this.api.get<ApiResponse>('/auth/login/qr/status', {
      params: { challengeToken },
    });
    return response.data.data;
  }

  async approveQrLogin(payload: { challengeToken: string; secret: string }): Promise<void> {
    await this.api.post('/auth/login/qr/approve', payload);
  }

  async denyQrLogin(payload: { challengeToken: string; secret: string }): Promise<void> {
    await this.api.post('/auth/login/qr/deny', payload);
  }

  async getLoginSecurity(): Promise<{ email2faEnabled: boolean }> {
    const response = await this.api.get<ApiResponse>('/auth/security');
    return response.data.data;
  }

  async updateLoginSecurity(payload: { email2faEnabled: boolean }): Promise<{ email2faEnabled: boolean }> {
    const response = await this.api.put<ApiResponse>('/auth/security', payload);
    return response.data.data;
  }

  async login(credentials: LoginForm): Promise<AuthResponse> {
    const response = await this.api.post<ApiResponse<AuthResponse>>('/auth/login', credentials);
    return response.data.data!;
  }

  async unifiedStaffLogin(credentials: LoginForm): Promise<UnifiedStaffLoginResponse> {
    const response = await this.api.post<ApiResponse<UnifiedStaffLoginResponse>>(
      '/auth/unified-staff-login',
      credentials
    );
    return response.data.data!;
  }

  async marketerLogin(credentials: LoginForm): Promise<any> {
    const response = await this.api.post<ApiResponse>('/auth/marketer/login', credentials);
    return response.data.data!;
  }

  async promoCodeAdminLogin(credentials: LoginForm): Promise<any> {
    const response = await this.api.post<ApiResponse>('/auth/promo-code-admin/login', credentials);
    return response.data.data!;
  }

  async register(data: RegisterForm): Promise<AuthResponse> {
    const refCode = getStoredRefCode();
    const response = await this.api.post<ApiResponse<AuthResponse>>('/auth/register', {
      ...data,
      refCode,
    });
    if (refCode) clearStoredRefCode();
    return response.data.data!;
  }

  async logout(): Promise<void> {
    await this.api.post('/auth/logout');
  }

  async getProfile(): Promise<any> {
    const response = await this.api.get<ApiResponse>('/auth/profile');
    return response.data.data;
  }

  /** Актуальные SA/Tester сессии, привязанные к текущему школьному пользователю. */
  async getLinkedSessions(): Promise<{ linkedSessions: any[] }> {
    const response = await this.api.get<ApiResponse>('/auth/linked-sessions');
    return response.data.data || { linkedSessions: [] };
  }

  async updateProfile(data: any): Promise<any> {
    const response = await this.api.put<ApiResponse>('/auth/profile', data);
    return response.data.data;
  }

  async createUser(data: any): Promise<any> {
    const response = await this.api.post<ApiResponse>('/auth/users', data);
    return response.data.data;
  }

  async updateUser(id: string, data: any): Promise<any> {
    const response = await this.api.put<ApiResponse>(`/auth/users/${id}`, data);
    return response.data.data;
  }

  async deleteUser(id: string): Promise<void> {
    await this.api.delete(`/auth/users/${id}`);
  }

  async changePassword(data: { currentPassword: string; newPassword: string }): Promise<void> {
    await this.api.post('/auth/change-password', data);
  }

  /** Смена пароля в ЛК ученика/родителя. */
  async changePortalPassword(data: {
    currentPassword: string;
    newPassword: string;
  }): Promise<{ message?: string }> {
    const response = await this.api.post<ApiResponse>('/client-auth/change-password', data);
    return response.data.data || { message: 'Пароль успешно изменён' };
  }

  async getAuthSessions(): Promise<{
    sessions: Array<{
      id: string;
      deviceLabel: string | null;
      userAgent: string | null;
      ipAddress: string | null;
      createdAt: string;
      lastSeenAt: string;
      expiresAt: string;
      current: boolean;
    }>;
  }> {
    const response = await this.api.get<ApiResponse>('/auth/sessions');
    return response.data.data || { sessions: [] };
  }

  async revokeAuthSession(sessionId: string): Promise<void> {
    await this.api.delete(`/auth/sessions/${sessionId}`);
  }

  async revokeOtherAuthSessions(): Promise<{ revoked: number }> {
    const response = await this.api.delete<ApiResponse>('/auth/sessions/others');
    return response.data.data || { revoked: 0 };
  }

  async revokeAllAuthSessions(): Promise<{ revoked: number }> {
    const response = await this.api.delete<ApiResponse>('/auth/sessions');
    return response.data.data || { revoked: 0 };
  }

  async changeEmail(data: { newEmail: string; password: string }): Promise<void> {
    await this.api.post('/auth/change-email', data);
  }

  async requestPasswordReset(email: string): Promise<{ message?: string }> {
    const response = await this.api.post<ApiResponse>('/auth/password-reset/request', { email });
    return response.data.data || { message: response.data.message };
  }

  async verifyPasswordResetCode(email: string, code: string): Promise<{
    resetToken: string;
    accounts: PublicAccount[];
  }> {
    const response = await this.api.post<ApiResponse>('/auth/password-reset/verify-code', { email, code });
    return response.data.data;
  }

  async confirmPasswordReset(data: {
    resetToken: string;
    newPassword: string;
    accountType: string;
    accountId: string;
  }): Promise<void> {
    await this.api.post('/auth/password-reset/confirm', data);
  }

  async sendEmailVerification(): Promise<{ message?: string; email?: string; alreadyVerified?: boolean }> {
    const response = await this.api.post<ApiResponse>('/auth/email/send-verification');
    return response.data.data || {};
  }

  async verifyEmailCode(code: string): Promise<{
    emailVerified?: boolean;
    isAccountApproved?: boolean;
    message?: string;
  }> {
    const response = await this.api.post<ApiResponse>('/auth/email/verify', { code });
    return response.data.data || {};
  }

  /** @deprecated */
  async resetPassword(_data: { token: string; newPassword: string }): Promise<void> {
    await this.api.post('/auth/reset-password', _data);
  }

  // Client endpoints
  async getClients(params?: any, signal?: AbortSignal, useCache: boolean = true): Promise<{ data: any[]; pagination: any }> {
    const cacheKey = generateCacheKey('/clients', params);
    
    if (useCache && !signal) {
      const cached = apiCache.get<{ data: any[]; pagination: any }>(cacheKey);
      if (cached) {
        return cached;
      }
    }
    
    const response = await this.api.get<ApiResponse>('/clients', { params, signal });
    const result = {
      data: (response.data.data || []).map((c: any) => ({
        ...c,
        balance: Number(c?.balance ?? 0),
      })),
      pagination: response.data.pagination
    };
    
    if (useCache && !signal) {
      apiCache.set(cacheKey, result, 2 * 60 * 1000); // Кэш на 2 минуты
    }
    
    return result;
  }

  async getClient(id: string): Promise<any> {
    const response = await this.api.get<ApiResponse>(`/clients/${id}`);
    return response.data.data;
  }

  async createClient(data: any): Promise<any> {
    const response = await this.api.post<ApiResponse>('/clients', data);
    return response.data.data;
  }

  async assignClientTrial(clientId: string, trainingId: string): Promise<any> {
    const response = await this.api.post<ApiResponse>(`/clients/${clientId}/trial`, { trainingId });
    return response.data.data;
  }

  async updateClient(id: string, data: any): Promise<any> {
    const response = await this.api.put<ApiResponse>(`/clients/${id}`, data);
    return response.data.data;
  }

  async updateClientMembershipFeeStatus(id: string, membershipFeePaid: boolean): Promise<any> {
    const response = await this.api.put<ApiResponse>(`/clients/${id}/membership-fee`, { membershipFeePaid });
    return response.data.data;
  }

  async getClientDeletionImpact(id: string): Promise<Record<string, number>> {
    const response = await this.api.get<ApiResponse>(`/clients/${id}/deletion-impact`);
    return response.data.data || {};
  }

  async deleteClient(id: string, purge?: Record<string, boolean>): Promise<void> {
    await this.api.delete(`/clients/${id}`, { data: { purge: purge || {} } });
  }

  async exportClients(): Promise<Blob> {
    const response = await this.api.get('/clients/export/excel', {
      responseType: 'blob'
    });
    return response.data;
  }

  async downloadClientTemplate(): Promise<Blob> {
    const response = await this.api.get('/clients/export/template', {
      responseType: 'blob'
    });
    return response.data;
  }

  async importClients(file: File): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await this.api.post<ApiResponse>('/clients/import/excel', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  }

  async addAchievement(clientId: string, data: any): Promise<any> {
    const response = await this.api.post<ApiResponse>(`/clients/${clientId}/achievements`, data);
    return response.data.data;
  }

  async removeAchievement(clientId: string, achievementId: string): Promise<void> {
    await this.api.delete(`/clients/${clientId}/achievements/${achievementId}`);
  }

  async getClientStats(id: string): Promise<any> {
    const response = await this.api.get<ApiResponse>(`/clients/${id}/stats`);
    return response.data.data;
  }

  // Trainer endpoints
  async getTrainers(params?: any, signal?: AbortSignal, useCache: boolean = true): Promise<{ data: any[]; pagination: any }> {
    const cacheKey = generateCacheKey('/trainers', params);
    
    if (useCache && !signal) {
      const cached = apiCache.get<{ data: any[]; pagination: any }>(cacheKey);
      if (cached) {
        return cached;
      }
    }
    
    const response = await this.api.get<ApiResponse>('/trainers', { params, signal });
    const result = {
      data: response.data.data || [],
      pagination: response.data.pagination
    };
    
    if (useCache && !signal) {
      apiCache.set(cacheKey, result, 5 * 60 * 1000); // Кэш на 5 минут
    }
    
    return result;
  }

  async getTrainer(id: string): Promise<any> {
    const response = await this.api.get<ApiResponse>(`/trainers/${id}`);
    return response.data.data;
  }

  async createTrainer(data: any): Promise<any> {
    const response = await this.api.post<ApiResponse>('/trainers', data);
    return response.data.data;
  }

  /** OWNER: создать Trainer-профиль на своём User */
  async createSelfAsTrainer(data: any): Promise<any> {
    const response = await this.api.post<ApiResponse>('/trainers/me', data);
    return response.data.data;
  }

  async updateTrainer(id: string, data: any): Promise<any> {
    const response = await this.api.put<ApiResponse>(`/trainers/${id}`, data);
    return response.data.data;
  }

  async getTrainerDeletionImpact(id: string): Promise<Record<string, number>> {
    const response = await this.api.get<ApiResponse>(`/trainers/${id}/deletion-impact`);
    return response.data.data || {};
  }

  async deleteTrainer(id: string, purge?: Record<string, boolean>): Promise<void> {
    await this.api.delete(`/trainers/${id}`, { data: { purge: purge || {} } });
  }

  async listTrainerDocuments(trainerId: string): Promise<any[]> {
    const response = await this.api.get<ApiResponse>(`/trainers/${trainerId}/documents`);
    return response.data.data || [];
  }

  async uploadTrainerDocument(
    trainerId: string,
    file: File,
    meta?: { title?: string; kind?: string }
  ): Promise<any> {
    const form = new FormData();
    form.append('file', file);
    if (meta?.title) form.append('title', meta.title);
    if (meta?.kind) form.append('kind', meta.kind);
    const response = await this.api.post<ApiResponse>(`/trainers/${trainerId}/documents`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data.data;
  }

  async downloadTrainerDocumentBlob(trainerId: string, docId: string): Promise<Blob> {
    const response = await this.api.get(`/trainers/${trainerId}/documents/${docId}/download`, {
      responseType: 'blob',
    });
    return response.data;
  }

  async deleteTrainerDocument(trainerId: string, docId: string): Promise<void> {
    await this.api.delete(`/trainers/${trainerId}/documents/${docId}`);
  }

  async addBranchToTrainer(trainerId: string, branchId: string): Promise<any> {
    const response = await this.api.post<ApiResponse>(`/trainers/${trainerId}/branches`, { branchId });
    return response.data.data;
  }

  async removeBranchFromTrainer(trainerId: string, branchId: string): Promise<void> {
    await this.api.delete(`/trainers/${trainerId}/branches/${branchId}`);
  }

  async getTrainerEarnings(trainerId: string, params?: any): Promise<any> {
    const response = await this.api.get<ApiResponse>(`/trainers/${trainerId}/earnings`, { params });
    return response.data.data;
  }

  async getAllTrainersEarnings(params?: any): Promise<any> {
    const response = await this.api.get<ApiResponse>('/trainers/earnings/all', { params });
    return response.data.data;
  }

  async getTrainerSalaryLedger(trainerId: string, params?: any): Promise<any> {
    const response = await this.api.get<ApiResponse>(`/trainers/${trainerId}/salary-ledger`, { params });
    return response.data.data;
  }

  async postTrainerSalaryLedger(trainerId: string, data: any): Promise<any> {
    const response = await this.api.post<ApiResponse>(`/trainers/${trainerId}/salary-ledger`, data);
    return response.data.data;
  }

  async getSalaryPayoutReminder(): Promise<any> {
    const response = await this.api.get<ApiResponse>('/trainers/salary-payout-reminder');
    return response.data.data;
  }

  async accrueFixedMonthlySalaries(): Promise<any> {
    const response = await this.api.post<ApiResponse>('/trainers/accrue-fixed-monthly');
    return response.data.data;
  }

  async getTrainerNotificationSettings(trainerId: string): Promise<any> {
    const response = await this.api.get<ApiResponse>(`/trainers/${trainerId}/notifications`);
    return response.data.data;
  }

  async updateTrainerNotificationSettings(trainerId: string, data: any): Promise<any> {
    const response = await this.api.put<ApiResponse>(`/trainers/${trainerId}/notifications`, data);
    return response.data.data;
  }

  // Push notification endpoints
  async getVapidKey(): Promise<string> {
    const response = await this.api.get<ApiResponse>('/push-notifications/vapid-key');
    return response.data.data.publicKey;
  }

  async subscribeToPush(subscription: any, userAgent?: string): Promise<any> {
    const response = await this.api.post<ApiResponse>('/push-notifications/subscribe', {
      subscription,
      userAgent
    });
    return response.data;
  }

  async unsubscribeFromPush(endpoint: string): Promise<any> {
    const response = await this.api.post<ApiResponse>('/push-notifications/unsubscribe', {
      endpoint
    });
    return response.data;
  }

  async getUserPushSubscriptions(): Promise<any[]> {
    const response = await this.api.get<ApiResponse>('/push-notifications/subscriptions');
    return response.data.data;
  }

  // Notification preferences (chat / changelog / schedule)
  async getSchoolNotificationPrefs(): Promise<NotificationPrefs> {
    const response = await this.api.get<ApiResponse>('/notification-prefs');
    return response.data.data;
  }

  async updateSchoolNotificationPrefs(data: Partial<NotificationPrefs>): Promise<NotificationPrefs> {
    const response = await this.api.put<ApiResponse>('/notification-prefs', data);
    return response.data.data;
  }

  async getPortalNotificationPrefs(): Promise<NotificationPrefs> {
    const response = await this.api.get<ApiResponse>('/client-auth/notification-prefs');
    return response.data.data;
  }

  async updatePortalNotificationPrefs(data: Partial<NotificationPrefs>): Promise<NotificationPrefs> {
    const response = await this.api.put<ApiResponse>('/client-auth/notification-prefs', data);
    return response.data.data;
  }

  async getPortalPushVapidKey(): Promise<string | null> {
    const response = await this.api.get<ApiResponse>('/client-auth/push/vapid-key');
    return response.data.data?.publicKey || null;
  }

  async subscribePortalPush(subscription: any, userAgent?: string): Promise<any> {
    const response = await this.api.post<ApiResponse>('/client-auth/push/subscribe', {
      subscription,
      userAgent,
    });
    return response.data;
  }

  async unsubscribePortalPush(endpoint: string): Promise<any> {
    const response = await this.api.post<ApiResponse>('/client-auth/push/unsubscribe', {
      endpoint,
    });
    return response.data;
  }

  async getPortalPushStatus(): Promise<{ subscribed: boolean; count: number }> {
    const response = await this.api.get<ApiResponse>('/client-auth/push/status');
    return response.data.data;
  }

  async getSuperAdminNotificationPrefs(): Promise<NotificationPrefs> {
    const response = await this.api.get<ApiResponse>('/admin-dashboard/notification-prefs');
    return response.data.data;
  }

  async updateSuperAdminNotificationPrefs(data: Partial<NotificationPrefs>): Promise<NotificationPrefs> {
    const response = await this.api.put<ApiResponse>('/admin-dashboard/notification-prefs', data);
    return response.data.data;
  }

  async getTesterNotificationPrefs(): Promise<NotificationPrefs> {
    const response = await this.api.get<ApiResponse>('/platform/tester/notification-prefs');
    return response.data.data;
  }

  async updateTesterNotificationPrefs(data: Partial<NotificationPrefs>): Promise<NotificationPrefs> {
    const response = await this.api.put<ApiResponse>('/platform/tester/notification-prefs', data);
    return response.data.data;
  }

  async getSchoolNotifications(): Promise<{
    items: any[];
    aggregates: Array<{ category: string; title: string; count: number; latestAt: string | null; url?: string }>;
    unreadTotal: number;
  }> {
    const response = await this.api.get<ApiResponse>('/notifications');
    return response.data.data;
  }

  async getSchoolNotificationsUnreadCount(): Promise<{ unreadTotal: number }> {
    const response = await this.api.get<ApiResponse>('/notifications/unread-count');
    return response.data.data!;
  }

  async markSchoolNotificationsRead(payload: {
    ids?: string[];
    category?: string;
    all?: boolean;
  }): Promise<{ count: number }> {
    const response = await this.api.post<ApiResponse>('/notifications/read', payload);
    return response.data.data;
  }

  async getPortalNotifications(): Promise<{
    items: any[];
    aggregates: Array<{ category: string; title: string; count: number; latestAt: string | null; url?: string }>;
    unreadTotal: number;
  }> {
    const response = await this.api.get<ApiResponse>('/client-auth/notifications');
    return response.data.data;
  }

  async getPortalNotificationsUnreadCount(): Promise<{ unreadTotal: number }> {
    const response = await this.api.get<ApiResponse>('/client-auth/notifications/unread-count');
    return response.data.data!;
  }

  async markPortalNotificationsRead(payload: {
    ids?: string[];
    category?: string;
    all?: boolean;
  }): Promise<{ count: number }> {
    const response = await this.api.post<ApiResponse>('/client-auth/notifications/read', payload);
    return response.data.data;
  }

  /** Batch unread для слотов savedAccounts (токены в теле, без активной сессии). */
  async getSavedAccountsUnread(
    accounts: Array<{ id: string; token: string }>
  ): Promise<{ counts: Record<string, number> }> {
    const response = await this.api.post<ApiResponse>('/auth/saved-accounts/unread', { accounts });
    return response.data.data || { counts: {} };
  }

  async listSchoolOffers(): Promise<any[]> {
    const response = await this.api.get<ApiResponse>('/admin-dashboard/school-offers');
    return response.data.data || [];
  }

  async publishSchoolOffer(data: { title: string; body: string; url?: string | null }): Promise<any> {
    const response = await this.api.post<ApiResponse>('/admin-dashboard/school-offers', data);
    return response.data.data;
  }

  async listStaffNotes(visibility?: string): Promise<any[]> {
    const response = await this.api.get<ApiResponse>('/staff-workspace/notes', {
      params: visibility ? { visibility } : undefined,
    });
    return response.data.data || [];
  }

  async createStaffNote(data: {
    title: string;
    body: string;
    visibility: string;
    color?: string | null;
    categoryKey?: string;
    reminderKey?: string;
    pinned?: boolean;
    tags?: string[] | null;
    relatedTaskIds?: string[] | null;
    relatedLink?: { type: string; id: string; label?: string } | null;
  }): Promise<any> {
    const response = await this.api.post<ApiResponse>('/staff-workspace/notes', data);
    return response.data.data;
  }

  async updateStaffNote(
    id: string,
    data: Partial<{
      title: string;
      body: string;
      visibility: string;
      color: string | null;
      categoryKey: string;
      reminderKey: string;
      pinned: boolean;
      tags: string[] | null;
      relatedTaskIds: string[] | null;
      relatedLink: { type: string; id: string; label?: string } | null;
    }>
  ): Promise<any> {
    const response = await this.api.put<ApiResponse>(`/staff-workspace/notes/${id}`, data);
    return response.data.data;
  }

  async deleteStaffNote(id: string): Promise<void> {
    await this.api.delete(`/staff-workspace/notes/${id}`);
  }

  async duplicateStaffNote(id: string): Promise<any> {
    const response = await this.api.post<ApiResponse>(`/staff-workspace/notes/${id}/duplicate`);
    return response.data.data;
  }

  async uploadStaffNoteAttachment(noteId: string, file: File): Promise<any> {
    const form = new FormData();
    form.append('file', file);
    const response = await this.api.post<ApiResponse>(
      `/staff-workspace/notes/${noteId}/attachments`,
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data.data;
  }

  async deleteStaffNoteAttachment(noteId: string, attachmentId: string): Promise<void> {
    await this.api.delete(`/staff-workspace/notes/${noteId}/attachments/${attachmentId}`);
  }

  staffNoteAttachmentUrl(noteId: string, attachmentId: string): string {
    return `/api/staff-workspace/notes/${noteId}/attachments/${attachmentId}/file`;
  }

  async listStaffWorkspaceUsers(): Promise<any[]> {
    const response = await this.api.get<ApiResponse>('/staff-workspace/users');
    return response.data.data || [];
  }

  async listStaffTasks(params?: {
    status?: string;
    from?: string;
    to?: string;
    mine?: boolean;
    assignedByMe?: boolean;
    assignedToMe?: boolean;
    favorite?: boolean;
    archived?: boolean;
    projectKey?: string;
    relatedTrainingId?: string;
    relatedSchoolEventId?: string;
  }): Promise<any[]> {
    const response = await this.api.get<ApiResponse>('/staff-workspace/tasks', {
      params: params
        ? {
            ...(params.status ? { status: params.status } : {}),
            ...(params.from ? { from: params.from } : {}),
            ...(params.to ? { to: params.to } : {}),
            ...(params.mine ? { mine: '1' } : {}),
            ...(params.assignedByMe ? { assignedByMe: '1' } : {}),
            ...(params.assignedToMe ? { assignedToMe: '1' } : {}),
            ...(params.favorite ? { favorite: '1' } : {}),
            ...(params.archived ? { archived: '1' } : {}),
            ...(params.projectKey ? { projectKey: params.projectKey } : {}),
            ...(params.relatedTrainingId ? { relatedTrainingId: params.relatedTrainingId } : {}),
            ...(params.relatedSchoolEventId
              ? { relatedSchoolEventId: params.relatedSchoolEventId }
              : {}),
          }
        : undefined,
    });
    return response.data.data || [];
  }

  async createStaffTask(data: {
    title: string;
    body: string;
    dueAt?: string | null;
    assigneeIds: string[];
    createChat?: boolean;
    relatedTrainingId?: string | null;
    relatedSchoolEventId?: string | null;
    status?: string;
    priority?: string;
    projectKey?: string;
    favorite?: boolean;
    archived?: boolean;
    recurrence?: string;
    subtasks?: Array<{ id?: string; title: string; done?: boolean }>;
    notifyAssignees?: boolean;
  }): Promise<any> {
    const response = await this.api.post<ApiResponse>('/staff-workspace/tasks', data);
    return response.data.data;
  }

  async updateStaffTask(
    id: string,
    data: Partial<{
      title: string;
      body: string;
      dueAt: string | null;
      assigneeIds: string[];
      status: string;
      createChat: boolean;
      relatedTrainingId: string | null;
      relatedSchoolEventId: string | null;
      priority: string;
      projectKey: string;
      favorite: boolean;
      archived: boolean;
      recurrence: string;
      subtasks: Array<{ id?: string; title: string; done?: boolean }> | null;
      notifyAssignees: boolean;
    }>
  ): Promise<any> {
    const response = await this.api.put<ApiResponse>(`/staff-workspace/tasks/${id}`, data);
    return response.data.data;
  }

  async uploadStaffTaskAttachment(taskId: string, file: File): Promise<any> {
    const form = new FormData();
    form.append('file', file);
    const response = await this.api.post<ApiResponse>(
      `/staff-workspace/tasks/${taskId}/attachments`,
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data.data;
  }

  async deleteStaffTaskAttachment(taskId: string, attachmentId: string): Promise<void> {
    await this.api.delete(`/staff-workspace/tasks/${taskId}/attachments/${attachmentId}`);
  }

  staffTaskAttachmentUrl(taskId: string, attachmentId: string): string {
    return `/api/staff-workspace/tasks/${taskId}/attachments/${attachmentId}/file`;
  }

  async createStaffTaskComment(taskId: string, body: string): Promise<any> {
    const response = await this.api.post<ApiResponse>(
      `/staff-workspace/tasks/${taskId}/comments`,
      { body }
    );
    return response.data.data;
  }

  async deleteStaffTaskComment(taskId: string, commentId: string): Promise<void> {
    await this.api.delete(`/staff-workspace/tasks/${taskId}/comments/${commentId}`);
  }

  async getScheduleSummary(params?: {
    startDate?: string;
    endDate?: string;
    branchId?: string;
    hallId?: string;
    trainerId?: string;
    groupId?: string;
  }): Promise<any> {
    const response = await this.api.get<ApiResponse>('/reports/schedule-summary', { params });
    return response.data.data;
  }

  async listCalendarNotes(params: {
    trainingId?: string;
    schoolEventId?: string;
    competitionId?: string;
  }): Promise<any[]> {
    const response = await this.api.get<ApiResponse>('/calendar-notes', { params });
    return response.data.data || [];
  }

  async createCalendarNote(data: {
    body: string;
    trainingId?: string;
    schoolEventId?: string;
    competitionId?: string;
  }): Promise<any> {
    const response = await this.api.post<ApiResponse>('/calendar-notes', data);
    return response.data.data;
  }

  async updateCalendarNote(id: string, data: { body: string }): Promise<any> {
    const response = await this.api.put<ApiResponse>(`/calendar-notes/${id}`, data);
    return response.data.data;
  }

  async deleteCalendarNote(id: string): Promise<void> {
    await this.api.delete(`/calendar-notes/${id}`);
  }

  async updateStaffTaskStatus(id: string, status: string): Promise<any> {
    const response = await this.api.patch<ApiResponse>(`/staff-workspace/tasks/${id}/status`, {
      status,
    });
    return response.data.data;
  }

  async deleteStaffTask(id: string): Promise<void> {
    await this.api.delete(`/staff-workspace/tasks/${id}`);
  }

  async ensureStaffTaskChat(taskId: string): Promise<any> {
    const response = await this.api.post<ApiResponse>(`/staff-workspace/tasks/${taskId}/chat`);
    return response.data.data;
  }

  // ——— Training plan ———
  async listTrainingPlanGroups(): Promise<any[]> {
    const response = await this.api.get<ApiResponse>('/training-plan/groups');
    return response.data.data || [];
  }

  async listExercises(q?: string): Promise<any[]> {
    const response = await this.api.get<ApiResponse>('/training-plan/exercises', {
      params: q ? { q } : undefined,
    });
    return response.data.data || [];
  }

  async createExercise(data: {
    title: string;
    description?: string;
    category?: string;
    exerciseType?: string | null;
    sets?: number | null;
    reps?: number | null;
    restSec?: number | null;
    difficulty?: string | null;
    inventory?: string | null;
    tempo?: string | null;
    videoUrl?: string | null;
    tags?: string[] | string | null;
  }): Promise<any> {
    const response = await this.api.post<ApiResponse>('/training-plan/exercises', data);
    return response.data.data;
  }

  async updateExercise(id: string, data: Partial<{
    title: string;
    description: string;
    category: string;
    exerciseType: string | null;
    sets: number | null;
    reps: number | null;
    restSec: number | null;
    difficulty: string | null;
    inventory: string | null;
    tempo: string | null;
    videoUrl: string | null;
    tags: string[] | string | null;
  }>): Promise<any> {
    const response = await this.api.put<ApiResponse>(`/training-plan/exercises/${id}`, data);
    return response.data.data;
  }

  async deleteExercise(id: string): Promise<void> {
    await this.api.delete(`/training-plan/exercises/${id}`);
  }

  async uploadExerciseMedia(exerciseId: string, file: File): Promise<any> {
    const form = new FormData();
    form.append('file', file);
    const response = await this.api.post<ApiResponse>(
      `/training-plan/exercises/${exerciseId}/media`,
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data.data;
  }

  async deleteExerciseMedia(exerciseId: string, mediaId: string): Promise<void> {
    await this.api.delete(`/training-plan/exercises/${exerciseId}/media/${mediaId}`);
  }

  async getExerciseMediaBlob(mediaId: string): Promise<Blob> {
    const response = await this.api.get(`/training-plan/media/${mediaId}`, {
      responseType: 'blob',
    });
    return response.data;
  }

  async listWorkoutTemplates(kind?: string): Promise<any[]> {
    const response = await this.api.get<ApiResponse>('/training-plan/templates', {
      params: kind ? { kind } : undefined,
    });
    return response.data.data || [];
  }

  async createWorkoutTemplate(data: any): Promise<any> {
    const response = await this.api.post<ApiResponse>('/training-plan/templates', data);
    return response.data.data;
  }

  async updateWorkoutTemplate(id: string, data: any): Promise<any> {
    const response = await this.api.put<ApiResponse>(`/training-plan/templates/${id}`, data);
    return response.data.data;
  }

  async deleteWorkoutTemplate(id: string): Promise<void> {
    await this.api.delete(`/training-plan/templates/${id}`);
  }

  async listTrainingCycles(): Promise<any[]> {
    const response = await this.api.get<ApiResponse>('/training-plan/cycles');
    return response.data.data || [];
  }

  async createTrainingCycle(data: {
    title: string;
    description?: string | null;
    frequency?: 'WEEKLY' | 'MONTHLY' | 'CUSTOM';
    quantity?: number | null;
    dateFrom: string;
    dateTo: string;
    groupIds: string[];
  }): Promise<any> {
    const response = await this.api.post<ApiResponse>('/training-plan/cycles', data);
    return response.data.data;
  }

  async previewTrainingCycleDraft(data: {
    dateFrom: string;
    dateTo: string;
    groupIds: string[];
  }): Promise<any> {
    const response = await this.api.post<ApiResponse>('/training-plan/cycles/preview', data);
    return response.data.data;
  }

  async getTrainingCycle(id: string): Promise<any> {
    const response = await this.api.get<ApiResponse>(`/training-plan/cycles/${id}`);
    return response.data.data;
  }

  async deleteTrainingCycle(id: string): Promise<void> {
    await this.api.delete(`/training-plan/cycles/${id}`);
  }

  async getSessionPlanByTraining(trainingId: string): Promise<any | null> {
    const response = await this.api.get<ApiResponse>(
      `/training-plan/session-plans/by-training/${trainingId}`
    );
    return response.data.data;
  }

  async upsertSessionPlan(trainingId: string, data: any): Promise<any> {
    const response = await this.api.put<ApiResponse>(
      `/training-plan/session-plans/${trainingId}`,
      data
    );
    return response.data.data;
  }

  async applyTemplateToSession(
    trainingId: string,
    data: { templateId: string; section: string; cycleId?: string; replace?: boolean }
  ): Promise<any> {
    const response = await this.api.post<ApiResponse>(
      `/training-plan/session-plans/${trainingId}/apply-template`,
      data
    );
    return response.data.data;
  }

  async getTesterPushVapidKey(): Promise<string | null> {
    const response = await this.api.get<ApiResponse>('/platform/tester/push/vapid-key');
    return response.data.data?.publicKey || null;
  }

  async subscribeTesterPush(subscription: any, userAgent?: string): Promise<any> {
    const response = await this.api.post<ApiResponse>('/platform/tester/push/subscribe', {
      subscription,
      userAgent,
    });
    return response.data;
  }

  async unsubscribeTesterPush(endpoint: string): Promise<any> {
    const response = await this.api.post<ApiResponse>('/platform/tester/push/unsubscribe', {
      endpoint,
    });
    return response.data;
  }

  async getTesterPushStatus(): Promise<{ subscribed: boolean; count: number }> {
    const response = await this.api.get<ApiResponse>('/platform/tester/push/status');
    return response.data.data;
  }

  async getMaintenanceStatus(): Promise<{
    maintenance: { enabled: boolean; message: string; scheduledAt?: string | null };
    testing: { enabled: boolean; message: string };
    closedTesting: { enabled: boolean; message: string };
    /** @deprecated use maintenance.enabled */
    enabled?: boolean;
    message?: string;
  }> {
    const response = await this.api.get<ApiResponse>('/maintenance/status');
    const data = response.data.data || {};
    // backward-compatible shape for older callers
    if (data.maintenance) {
      return {
        ...data,
        closedTesting: data.closedTesting || { enabled: false, message: '' },
        enabled: Boolean(data.maintenance.enabled),
        message: data.maintenance.message,
      };
    }
    return {
      maintenance: { enabled: Boolean(data.enabled), message: data.message || '' },
      testing: { enabled: false, message: '' },
      closedTesting: { enabled: false, message: '' },
      enabled: Boolean(data.enabled),
      message: data.message,
    };
  }

  async getMaintenanceAccess(): Promise<{
    canAccess: boolean;
    mode: 'none' | 'maintenance' | 'testing' | 'closed_testing';
    message: string;
    isSuperAdmin: boolean;
    isAllowlisted: boolean;
  }> {
    const response = await this.api.get<ApiResponse>('/maintenance/access');
    return response.data.data;
  }

  async getAdminMaintenance(): Promise<{ enabled: boolean; message: string; scheduledAt?: string | null }> {
    const response = await this.api.get<ApiResponse>('/admin-dashboard/maintenance');
    return response.data.data;
  }

  async updateAdminMaintenance(enabled: boolean): Promise<{ enabled: boolean; message: string }> {
    const response = await this.api.put<ApiResponse>('/admin-dashboard/maintenance', { enabled });
    return response.data.data;
  }

  async updateAdminMaintenanceSchedule(scheduledAt: string | null): Promise<{
    enabled: boolean;
    scheduledAt: string | null;
  }> {
    const response = await this.api.put<ApiResponse>('/admin-dashboard/maintenance/schedule', { scheduledAt });
    return response.data.data;
  }

  async getAdminPlatformChatSettings(): Promise<{
    platformChatMessageDeleteLimitMinutes: number;
  }> {
    const response = await this.api.get<ApiResponse>('/admin-dashboard/platform-chat-settings');
    return response.data.data;
  }

  async updateAdminPlatformChatSettings(data: {
    platformChatMessageDeleteLimitMinutes: number;
  }): Promise<{ platformChatMessageDeleteLimitMinutes: number }> {
    const response = await this.api.put<ApiResponse>(
      '/admin-dashboard/platform-chat-settings',
      data
    );
    return response.data.data;
  }

  async getAdminTestingMode(): Promise<{
    enabled: boolean;
    message: string;
    allowlist: string[];
    closedTesting?: { enabled: boolean; message: string };
  }> {
    const response = await this.api.get<ApiResponse>('/admin-dashboard/testing-mode');
    return response.data.data;
  }

  async getTestingAccountCandidates(): Promise<
    Array<{ email: string; name: string; roleLabel: string }>
  > {
    const response = await this.api.get<ApiResponse>('/admin-dashboard/testing-mode/accounts');
    return response.data.data?.accounts || [];
  }

  async updateAdminTestingMode(
    enabled: boolean,
    allowlist?: string[]
  ): Promise<{ enabled: boolean; message: string; allowlist: string[] }> {
    const response = await this.api.put<ApiResponse>('/admin-dashboard/testing-mode', {
      enabled,
      allowlist,
    });
    return response.data.data;
  }

  /** Проверка доступа токена слота (не текущего) при testing/maintenance. */
  async getMaintenanceAccessWithToken(token: string): Promise<{
    canAccess: boolean;
    mode: string;
    isSuperAdmin?: boolean;
    isAllowlisted?: boolean;
  }> {
    const response = await this.api.get<ApiResponse>('/maintenance/access', {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data.data;
  }

  async getPublicTerms(): Promise<{ content: string; updatedAt: string | null; isDefault: boolean }> {
    const response = await this.api.get<ApiResponse>('/legal/terms');
    return response.data.data;
  }

  async getAdminTerms(): Promise<{ content: string; updatedAt: string | null; isDefault: boolean }> {
    const response = await this.api.get<ApiResponse>('/admin-dashboard/terms');
    return response.data.data;
  }

  async updateAdminTerms(
    content: string
  ): Promise<{ content: string; updatedAt: string | null; isDefault: boolean }> {
    const response = await this.api.put<ApiResponse>('/admin-dashboard/terms', { content });
    return response.data.data;
  }

  async getPublicPrivacy(): Promise<{ content: string; updatedAt: string | null; isDefault: boolean }> {
    const response = await this.api.get<ApiResponse>('/legal/privacy');
    return response.data.data;
  }

  async getAdminPrivacy(): Promise<{ content: string; updatedAt: string | null; isDefault: boolean }> {
    const response = await this.api.get<ApiResponse>('/admin-dashboard/privacy');
    return response.data.data;
  }

  async updateAdminPrivacy(
    content: string
  ): Promise<{ content: string; updatedAt: string | null; isDefault: boolean }> {
    const response = await this.api.put<ApiResponse>('/admin-dashboard/privacy', { content });
    return response.data.data;
  }

  // Standard endpoints (Нормативы)
  async getStandards(params?: any): Promise<{ data: any[] }> {
    const response = await this.api.get<ApiResponse>('/standards', { params });
    return {
      data: response.data.data || []
    };
  }

  async getStandard(id: string): Promise<any> {
    const response = await this.api.get<ApiResponse>(`/standards/${id}`);
    return response.data.data;
  }

  async createStandard(data: any): Promise<any> {
    const response = await this.api.post<ApiResponse>('/standards', data);
    return response.data.data;
  }

  async updateStandard(id: string, data: any): Promise<any> {
    const response = await this.api.put<ApiResponse>(`/standards/${id}`, data);
    return response.data.data;
  }

  async deleteStandard(id: string): Promise<void> {
    await this.api.delete(`/standards/${id}`);
  }

  // Client Standards endpoints (Выполнения нормативов клиентом)
  async getClientStandards(clientId: string): Promise<{ data: any[] }> {
    const response = await this.api.get<ApiResponse>(`/standards/clients/${clientId}`);
    return {
      data: response.data.data || []
    };
  }

  async addClientStandard(clientId: string, data: any): Promise<any> {
    const response = await this.api.post<ApiResponse>(`/standards/clients/${clientId}`, data);
    return response.data.data;
  }

  async updateClientStandard(clientId: string, clientStandardId: string, data: any): Promise<any> {
    const response = await this.api.put<ApiResponse>(`/standards/clients/${clientId}/${clientStandardId}`, data);
    return response.data.data;
  }

  async deleteClientStandard(clientId: string, clientStandardId: string): Promise<void> {
    await this.api.delete(`/standards/clients/${clientId}/${clientStandardId}`);
  }

  // Group endpoints
  async getGroups(params?: any, signal?: AbortSignal, useCache: boolean = true): Promise<{ data: any[]; pagination: any }> {
    const cacheKey = generateCacheKey('/groups', params);
    
    if (useCache && !signal) {
      const cached = apiCache.get<{ data: any[]; pagination: any }>(cacheKey);
      if (cached) {
        return cached;
      }
    }
    
    const response = await this.api.get<ApiResponse>('/groups', { params, signal });
    const result = {
      data: response.data.data || [],
      pagination: response.data.pagination
    };
    
    if (useCache && !signal) {
      apiCache.set(cacheKey, result, 5 * 60 * 1000); // Кэш на 5 минут
    }
    
    return result;
  }

  async getGroup(id: string): Promise<any> {
    const response = await this.api.get<ApiResponse>(`/groups/${id}`);
    return response.data.data;
  }

  async createGroup(data: any): Promise<any> {
    const response = await this.api.post<ApiResponse>('/groups', data);
    return response.data.data;
  }

  async updateGroup(id: string, data: any): Promise<any> {
    const response = await this.api.put<ApiResponse>(`/groups/${id}`, data);
    return response.data.data;
  }

  async getGroupDeletionImpact(id: string): Promise<Record<string, number>> {
    const response = await this.api.get<ApiResponse>(`/groups/${id}/deletion-impact`);
    return response.data.data || {};
  }

  async deleteGroup(id: string, purge?: Record<string, boolean>): Promise<void> {
    await this.api.delete(`/groups/${id}`, { data: { purge: purge || {} } });
  }

  async addClientToGroup(groupId: string, clientId: string, billingEffectiveFrom?: string): Promise<any> {
    const response = await this.api.post<ApiResponse>(`/groups/${groupId}/clients`, {
      clientId,
      ...(billingEffectiveFrom ? { billingEffectiveFrom } : {}),
    });
    return response.data.data;
  }

  async removeClientFromGroup(groupId: string, clientId: string): Promise<void> {
    await this.api.delete(`/groups/${groupId}/clients/${clientId}`);
  }

  // Branch endpoints
  async getBranches(params?: any, signal?: AbortSignal, useCache: boolean = true): Promise<{ data: any[]; pagination: any }> {
    const cacheKey = generateCacheKey('/branches', params);
    
    if (useCache && !signal) {
      const cached = apiCache.get<{ data: any[]; pagination: any }>(cacheKey);
      if (cached) {
        return cached;
      }
    }
    
    const response = await this.api.get<ApiResponse>('/branches', { params, signal });
    const result = {
      data: response.data.data || [],
      pagination: response.data.pagination
    };
    
    if (useCache && !signal) {
      apiCache.set(cacheKey, result, 10 * 60 * 1000); // Кэш на 10 минут (филиалы редко меняются)
    }
    
    return result;
  }

  async getBranch(id: string): Promise<any> {
    const response = await this.api.get<ApiResponse>(`/branches/${id}`);
    return response.data.data;
  }

  async createBranch(data: any): Promise<any> {
    const response = await this.api.post<ApiResponse>('/branches', data);
    return response.data.data;
  }

  async updateBranch(id: string, data: any): Promise<any> {
    const response = await this.api.put<ApiResponse>(`/branches/${id}`, data);
    return response.data.data;
  }

  async getBranchDeletionImpact(id: string): Promise<Record<string, number>> {
    const response = await this.api.get<ApiResponse>(`/branches/${id}/deletion-impact`);
    return response.data.data || {};
  }

  async deleteBranch(id: string, purge?: Record<string, boolean>): Promise<void> {
    await this.api.delete(`/branches/${id}`, { data: { purge: purge || {} } });
  }

  // Hall endpoints
  async getHalls(params?: any, signal?: AbortSignal, useCache: boolean = true): Promise<{ data: any[]; pagination: any }> {
    const cacheKey = generateCacheKey('/halls', params);
    
    if (useCache && !signal) {
      const cached = apiCache.get<{ data: any[]; pagination: any }>(cacheKey);
      if (cached) {
        return cached;
      }
    }
    
    const response = await this.api.get<ApiResponse>('/halls', { params, signal });
    const result = {
      data: response.data.data || [],
      pagination: response.data.pagination
    };
    
    if (useCache && !signal) {
      apiCache.set(cacheKey, result, 10 * 60 * 1000); // Кэш на 10 минут
    }
    
    return result;
  }

  async getHall(id: string): Promise<any> {
    const response = await this.api.get<ApiResponse>(`/halls/${id}`);
    return response.data.data;
  }

  async createHall(data: any): Promise<any> {
    const response = await this.api.post<ApiResponse>('/halls', data);
    return response.data.data;
  }

  async updateHall(id: string, data: any): Promise<any> {
    const response = await this.api.put<ApiResponse>(`/halls/${id}`, data);
    return response.data.data;
  }

  async deleteHall(id: string): Promise<void> {
    await this.api.delete(`/halls/${id}`);
  }

  // Training endpoints
  async getTrainings(params?: any, signal?: AbortSignal): Promise<{ data: any[]; pagination: any }> {
    const response = await this.api.get<ApiResponse>('/trainings', { params, signal });
    return {
      data: response.data.data || [],
      pagination: response.data.pagination
    };
  }

  async getTraining(id: string): Promise<any> {
    const response = await this.api.get<ApiResponse>(`/trainings/${id}`);
    return response.data.data;
  }

  async createTraining(data: any): Promise<any> {
    const response = await this.api.post<ApiResponse>('/trainings', data);
    return response.data.data;
  }

  async createTrainingsBatch(trainings: any[]): Promise<{ created: any[]; failed: any[]; createdCount: number; failedCount: number }> {
    const response = await this.api.post<ApiResponse>('/trainings/batch', { trainings });
    return response.data.data;
  }

  async updateTraining(id: string, data: any): Promise<any> {
    const response = await this.api.put<ApiResponse>(`/trainings/${id}`, data);
    return response.data.data;
  }

  async deleteTraining(id: string): Promise<void> {
    await this.api.delete(`/trainings/${id}`);
  }

  async deleteTrainingsBatch(trainingIds: string[]): Promise<{ deletedCount: number }> {
    const response = await this.api.delete<ApiResponse>('/trainings/batch', { data: { trainingIds } });
    return response.data.data;
  }

  async removeDuplicateTrainings(): Promise<{ removedCount: number; removedIds: string[] }> {
    const response = await this.api.post<ApiResponse>('/trainings/remove-duplicates');
    return response.data.data;
  }

  // Competition endpoints
  async getCompetitions(params?: any, signal?: AbortSignal): Promise<{ data: any[]; pagination: any }> {
    const response = await this.api.get<ApiResponse>('/competitions', { params, signal });
    return {
      data: response.data.data || [],
      pagination: response.data.pagination
    };
  }

  async getCompetition(id: string): Promise<any> {
    const response = await this.api.get<ApiResponse>(`/competitions/${id}`);
    return response.data.data;
  }

  async createCompetition(data: any): Promise<any> {
    const response = await this.api.post<ApiResponse>('/competitions', data);
    return response.data.data;
  }

  async updateCompetition(id: string, data: any): Promise<any> {
    const response = await this.api.put<ApiResponse>(`/competitions/${id}`, data);
    return response.data.data;
  }

  async deleteCompetition(id: string): Promise<void> {
    await this.api.delete(`/competitions/${id}`);
  }

  async getSchoolEvents(params?: { startDate?: string; endDate?: string; type?: string }, signal?: AbortSignal): Promise<any[]> {
    const response = await this.api.get<ApiResponse>('/school-events', { params, signal });
    return response.data.data || [];
  }

  async getSchoolEvent(id: string): Promise<any> {
    const response = await this.api.get<ApiResponse>(`/school-events/${id}`);
    return response.data.data;
  }

  async createSchoolEvent(data: any): Promise<any> {
    const response = await this.api.post<ApiResponse>('/school-events', data);
    return response.data.data;
  }

  async updateSchoolEvent(id: string, data: any): Promise<any> {
    const response = await this.api.put<ApiResponse>(`/school-events/${id}`, data);
    return response.data.data;
  }

  async deleteSchoolEvent(id: string): Promise<void> {
    await this.api.delete(`/school-events/${id}`);
  }

  async addCompetitionResult(competitionId: string, data: any): Promise<any> {
    const response = await this.api.post<ApiResponse>(`/competitions/${competitionId}/results`, data);
    return response.data.data;
  }

  async updateCompetitionResult(id: string, data: any): Promise<any> {
    const response = await this.api.put<ApiResponse>(`/competitions/results/${id}`, data);
    return response.data.data;
  }

  async deleteCompetitionResult(id: string): Promise<void> {
    await this.api.delete(`/competitions/results/${id}`);
  }

  async updateCompetitionAttendance(competitionId: string, participantId: string, data: any): Promise<any> {
    const response = await this.api.put<ApiResponse>(`/competitions/${competitionId}/attendance/${participantId}`, data);
    return response.data.data;
  }

  async getTrainerConflicts(competitionId: string, params?: any): Promise<any[]> {
    const response = await this.api.get<ApiResponse>(`/competitions/${competitionId}/trainer-conflicts`, { params });
    return response.data.data || [];
  }

  // Attendance endpoints
  async getAttendances(params?: any): Promise<{ data: any[]; pagination: any }> {
    const response = await this.api.get<ApiResponse>('/attendances', { params });
    return {
      data: response.data.data || [],
      pagination: response.data.pagination
    };
  }

  async exportAttendanceExcel(params: {
    scope: 'trainer' | 'client' | 'group';
    id: string;
    from?: string;
    to?: string;
  }): Promise<{ blob: Blob; filename: string }> {
    const response = await this.api.get('/attendances/export/excel', {
      params,
      responseType: 'blob',
    });
    const disposition = String(response.headers['content-disposition'] || '');
    let filename = `attendance_${params.scope}_${params.from || ''}_${params.to || ''}.xlsx`;
    const utf8Match = disposition.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
    const plainMatch = disposition.match(/filename\s*=\s*"?([^";]+)"?/i);
    if (utf8Match?.[1]) {
      try {
        filename = decodeURIComponent(utf8Match[1].trim());
      } catch {
        filename = utf8Match[1].trim();
      }
    } else if (plainMatch?.[1]) {
      filename = plainMatch[1].trim();
    }
    return { blob: response.data, filename };
  }

  async getAttendance(id: string): Promise<any> {
    const response = await this.api.get<ApiResponse>(`/attendances/${id}`);
    return response.data.data;
  }

  private isLikelyOfflineError(e: any): boolean {
    return (
      !e?.response ||
      e?.code === 'ERR_NETWORK' ||
      e?.message === 'Network Error' ||
      (typeof navigator !== 'undefined' && navigator.onLine === false)
    );
  }

  async createAttendance(data: any): Promise<any> {
    try {
      const response = await this.api.post<ApiResponse>('/attendances', data);
      return response.data.data;
    } catch (e) {
      if (!this.isLikelyOfflineError(e)) throw e;
      const { queueAttendanceMark } = await import('../offline/offlineApi');
      await queueAttendanceMark({
        trainingId: String(data?.trainingId || ''),
        clientId: String(data?.clientId || ''),
        status: String(data?.status || 'PRESENT'),
        path: '/attendances',
        body: data,
      });
      return { ...data, queuedOffline: true };
    }
  }

  async updateAttendance(id: string, data: any): Promise<any> {
    try {
      const response = await this.api.put<ApiResponse>(`/attendances/${id}`, data);
      return response.data.data;
    } catch (e) {
      if (!this.isLikelyOfflineError(e)) throw e;
      const { queueAttendanceMark } = await import('../offline/offlineApi');
      await queueAttendanceMark({
        trainingId: String(data?.trainingId || ''),
        clientId: String(data?.clientId || id),
        status: String(data?.status || 'PRESENT'),
        path: `/attendances/${id}`,
        method: 'PUT',
        body: data,
      });
      return { ...data, id, queuedOffline: true };
    }
  }

  async deleteAttendance(id: string): Promise<void> {
    await this.api.delete(`/attendances/${id}`);
  }

  async getAttendancesByTraining(trainingId: string): Promise<any> {
    try {
      const response = await this.api.get<ApiResponse>(`/attendances/training/${trainingId}`);
      return response.data.data;
    } catch (e) {
      const { getCachedAttendances } = await import('../offline/offlineApi');
      const cached = await getCachedAttendances<any>();
      return cached.filter(
        (a) => String(a?.trainingId || '') === String(trainingId)
      );
    }
  }

  async bulkUpdateAttendance(trainingId: string, attendances: any[]): Promise<any> {
    try {
      const response = await this.api.post<ApiResponse>('/attendances/bulk', {
        trainingId,
        attendances
      });
      return response.data.data;
    } catch (e) {
      if (!this.isLikelyOfflineError(e)) throw e;
      const { queueAttendanceMark } = await import('../offline/offlineApi');
      for (const row of attendances || []) {
        await queueAttendanceMark({
          trainingId: String(trainingId),
          clientId: String(row?.clientId || ''),
          status: String(row?.status || 'PRESENT'),
          path: '/attendances/bulk',
          body: { trainingId, attendances: [row] },
        });
      }
      return { trainingId, attendances, queuedOffline: true };
    }
  }

  // Payment endpoints
  async getPayments(params?: any, signal?: AbortSignal): Promise<{ data: any[]; pagination: any }> {
    const response = await this.api.get<ApiResponse>('/payments', { params, signal });
    return {
      data: response.data.data || [],
      pagination: response.data.pagination
    };
  }

  async getPayment(id: string): Promise<any> {
    const response = await this.api.get<ApiResponse>(`/payments/${id}`);
    return response.data.data;
  }

  async createPayment(data: any): Promise<any> {
    const response = await this.api.post<ApiResponse>('/payments', data);
    return response.data.data;
  }

  async updatePayment(id: string, data: any): Promise<any> {
    const response = await this.api.put<ApiResponse>(`/payments/${id}`, data);
    return response.data.data;
  }

  async deletePayment(id: string): Promise<void> {
    await this.api.delete(`/payments/${id}`);
  }

  async recalculateMonthlyPayment(id: string, newAmount: number): Promise<any> {
    const response = await this.api.post<ApiResponse>(`/payments/${id}/recalculate`, { newAmount });
    return response.data.data;
  }

  async createMonthlyPayments(): Promise<any> {
    const response = await this.api.post<ApiResponse>('/payments/monthly/create');
    return response.data;
  }

  // Finance (раздел «Финансы»)
  async getFinanceTypes(): Promise<import('../types').FinanceOperationType[]> {
    const response = await this.api.get<ApiResponse>('/finance/types');
    return response.data.data || [];
  }

  async getFinanceRefs(): Promise<{
    types: import('../types').FinanceOperationType[];
    clients: import('../types').Client[];
    trainers: import('../types').Trainer[];
    groups: import('../types').Group[];
    branches: import('../types').Branch[];
  }> {
    const response = await this.api.get<ApiResponse>('/finance/refs');
    return response.data.data;
  }

  async createFinanceType(data: {
    name: string;
    code?: string;
    defaultDirection?: 'income' | 'expense';
  }): Promise<import('../types').FinanceOperationType> {
    const response = await this.api.post<ApiResponse>('/finance/types', data);
    return response.data.data;
  }

  async getFinanceOperations(params?: Record<string, any>): Promise<import('../types').FinanceOperationsResponse> {
    const response = await this.api.get<ApiResponse>('/finance/operations', { params });
    return (
      response.data.data || {
        items: [],
        total: 0,
        limit: 100,
        offset: 0,
      }
    );
  }

  async createFinanceOperation(data: {
    direction: 'income' | 'expense';
    typeCode: string;
    title: string;
    amount: number;
    occurredAt?: string;
    notes?: string;
    clientId?: string;
    trainerId?: string;
    groupId?: string;
    branchId?: string;
    allocation?: 'accrued' | 'paid' | 'debit' | 'credit';
  }): Promise<import('../types').FinanceOperation> {
    const response = await this.api.post<ApiResponse>('/finance/operations', data);
    // Баланс клиента мог измениться — сбрасываем кэш списка клиентов
    apiCache.invalidatePrefix('/clients');
    return response.data.data;
  }

  async deleteFinanceOperation(id: string): Promise<void> {
    await this.api.delete<ApiResponse>(`/finance/operations/${id}`);
    apiCache.invalidatePrefix('/clients');
  }

  async purgeUnlinkedFinanceHistory(): Promise<{
    deletedSalaryLedgers: number;
    cancelledPayments: number;
    resetClientBalances: number;
    trainersReset: number;
  }> {
    const response = await this.api.post<ApiResponse>('/finance/purge-unlinked-history');
    apiCache.invalidatePrefix('/clients');
    apiCache.invalidatePrefix('/finance');
    return response.data.data;
  }

  async getFinanceSalarySummary(params?: Record<string, any>): Promise<import('../types').FinanceSalaryRow[]> {
    const response = await this.api.get<ApiResponse>('/finance/salary-summary', { params });
    return response.data.data || [];
  }

  async payoutTrainerSalary(data: {
    trainerId: string;
    amount: number;
    periodLabel?: string;
    occurredAt?: string;
    notes?: string;
  }): Promise<import('../types').FinanceOperation> {
    const response = await this.api.post<ApiResponse>('/finance/salary-payout', data);
    return response.data.data;
  }

  async getFinanceMembershipSummary(
    params?: Record<string, any>
  ): Promise<import('../types').FinanceMembershipRow[]> {
    const response = await this.api.get<ApiResponse>('/finance/membership-summary', { params });
    return response.data.data || [];
  }

  async receiveMembershipPayment(data: {
    paymentId?: string;
    clientId?: string;
    amount?: number;
    notes?: string;
    periodKey?: string;
  }): Promise<any> {
    const response = await this.api.post<ApiResponse>('/finance/membership-payments/receive', data);
    apiCache.invalidatePrefix('/clients');
    return response.data.data;
  }

  async updateMembershipFinanceAmount(data: {
    paymentId: string;
    amount: number;
    notes?: string;
  }): Promise<any> {
    const response = await this.api.put<ApiResponse>('/finance/membership-payments/amount', data);
    apiCache.invalidatePrefix('/clients');
    return response.data.data;
  }

  async correctMembershipAccrual(data: {
    clientId: string;
    paymentId?: string;
    periodKey?: string;
    newAmount: number;
    reason: string;
    occurredAt?: string;
  }): Promise<any> {
    const response = await this.api.post<ApiResponse>(
      '/finance/membership-payments/correct-accrual',
      data
    );
    return response.data.data;
  }

  async changeMembershipPack(data: { clientId: string; membershipId: string }): Promise<any> {
    const response = await this.api.post<ApiResponse>('/finance/membership-payments/change-pack', data);
    return response.data.data;
  }

  async getMonthChargesBreakdown(params: {
    clientId?: string;
    month?: string;
    dateFrom?: string;
    dateTo?: string;
  }): Promise<any[]> {
    const response = await this.api.get<ApiResponse>('/finance/month-charges', { params });
    return response.data.data || [];
  }

  async listSchoolPaymentMethods(): Promise<any[]> {
    const response = await this.api.get<ApiResponse>('/finance/payment-methods');
    return response.data.data || [];
  }

  async createSchoolPaymentMethod(form: FormData): Promise<any> {
    const response = await this.api.post<ApiResponse>('/finance/payment-methods', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data.data;
  }

  async updateSchoolPaymentMethod(id: string, form: FormData): Promise<any> {
    const response = await this.api.put<ApiResponse>(`/finance/payment-methods/${id}`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data.data;
  }

  async deleteSchoolPaymentMethod(id: string): Promise<void> {
    await this.api.delete(`/finance/payment-methods/${id}`);
  }

  async getSchoolPaymentMethodQrBlob(id: string): Promise<Blob> {
    const response = await this.api.get(`/finance/payment-methods/${id}/qr`, {
      responseType: 'blob',
    });
    return response.data;
  }

  async listAwaitingPaymentReceipts(): Promise<any[]> {
    const response = await this.api.get<ApiResponse>('/finance/payment-receipts');
    return response.data.data || [];
  }

  async getPaymentReceiptBlob(paymentId: string): Promise<Blob> {
    const response = await this.api.get(`/finance/payment-receipts/${paymentId}/file`, {
      responseType: 'blob',
    });
    return response.data;
  }

  async confirmPaymentReceipt(paymentId: string): Promise<any> {
    const response = await this.api.post<ApiResponse>(
      `/finance/payment-receipts/${paymentId}/confirm`
    );
    return response.data.data;
  }

  async rejectPaymentReceipt(paymentId: string): Promise<any> {
    const response = await this.api.post<ApiResponse>(
      `/finance/payment-receipts/${paymentId}/reject`
    );
    return response.data.data;
  }

  // Membership endpoints
  async getMemberships(params?: any, signal?: AbortSignal): Promise<{ data: any[]; pagination: any }> {
    const response = await this.api.get<ApiResponse>('/memberships', { params, signal });
    return {
      data: response.data.data || [],
      pagination: response.data.pagination
    };
  }

  async getMembership(id: string): Promise<any> {
    const response = await this.api.get<ApiResponse>(`/memberships/${id}`);
    return response.data.data;
  }

  async createMembership(data: any): Promise<any> {
    const response = await this.api.post<ApiResponse>('/memberships', data);
    return response.data.data;
  }

  async updateMembership(id: string, data: any): Promise<any> {
    const response = await this.api.put<ApiResponse>(`/memberships/${id}`, data);
    return response.data.data;
  }

  async deleteMembership(id: string): Promise<void> {
    await this.api.delete(`/memberships/${id}`);
  }

  async getMembershipStats(): Promise<any> {
    const response = await this.api.get<ApiResponse>('/memberships/stats');
    return response.data.data;
  }

  async archiveMembership(id: string): Promise<any> {
    const response = await this.api.post<ApiResponse>(`/memberships/${id}/archive`);
    return response.data.data;
  }

  async restoreMembership(id: string): Promise<any> {
    const response = await this.api.post<ApiResponse>(`/memberships/${id}/restore`);
    return response.data.data;
  }

  async duplicateMembership(id: string): Promise<any> {
    const response = await this.api.post<ApiResponse>(`/memberships/${id}/duplicate`);
    return response.data.data;
  }

  async mergeMemberships(targetId: string, sourceIds: string[]): Promise<any> {
    const response = await this.api.post<ApiResponse>('/memberships/merge', {
      targetId,
      sourceIds,
    });
    return response.data.data;
  }

  // Client membership endpoints
  async getClientMemberships(params?: any): Promise<{ data: any[] }> {
    const response = await this.api.get<ApiResponse>('/client-memberships', { params });
    return {
      data: response.data.data || []
    };
  }

  async createClientMembership(data: any): Promise<any> {
    const response = await this.api.post<ApiResponse>('/client-memberships', data);
    apiCache.invalidatePrefix('/clients');
    return response.data.data;
  }

  async updateClientMembership(id: string, data: any): Promise<any> {
    const response = await this.api.put<ApiResponse>(`/client-memberships/${id}`, data);
    apiCache.invalidatePrefix('/clients');
    return response.data.data;
  }

  async markVisitUsed(id: string): Promise<any> {
    const response = await this.api.post<ApiResponse>(`/client-memberships/${id}/mark-visit`);
    apiCache.invalidatePrefix('/clients');
    return response.data.data;
  }

  async deleteClientMembership(id: string): Promise<void> {
    await this.api.delete(`/client-memberships/${id}`);
    apiCache.invalidatePrefix('/clients');
  }

  // Report endpoints
  async getDashboardStats(signal?: AbortSignal): Promise<any> {
    const response = await this.api.get<ApiResponse>('/reports/dashboard', { signal });
    return response.data.data;
  }

  async getRecentActivity(limit?: number, signal?: AbortSignal): Promise<any> {
    const response = await this.api.get<ApiResponse>('/reports/dashboard/activity', {
      params: { limit },
      signal
    });
    return response.data.data;
  }

  async getUpcomingTrainings(limit?: number, days?: number, signal?: AbortSignal): Promise<any> {
    const response = await this.api.get<ApiResponse>('/reports/dashboard/upcoming-trainings', {
      params: { limit, days },
      signal
    });
    return response.data.data;
  }

  async getRevenueReport(params?: any): Promise<any> {
    const response = await this.api.get<ApiResponse>('/reports/revenue', { params });
    return response.data.data;
  }

  async getAttendanceReport(params?: any): Promise<any> {
    const response = await this.api.get<ApiResponse>('/reports/attendance', { params });
    return response.data.data;
  }

  async getTrainerSalaryReport(params?: any): Promise<any> {
    const response = await this.api.get<ApiResponse>('/reports/trainer-salary', { params });
    return response.data.data;
  }

  // File upload
  async uploadFile(file: File, endpoint: string): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await this.api.post<ApiResponse>(endpoint, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data.data;
  }

  // Promo Code endpoints
  async getPromoCodes(params?: any, signal?: AbortSignal): Promise<{ data: any[]; pagination: any }> {
    const response = await this.api.get<ApiResponse>('/promo-codes', { params, signal });
    return {
      data: response.data.data || [],
      pagination: response.data.pagination
    };
  }

  async getPromoCode(id: string): Promise<any> {
    const response = await this.api.get<ApiResponse>(`/promo-codes/${id}`);
    return response.data.data;
  }

  async createPromoCode(data: any): Promise<any> {
    const response = await this.api.post<ApiResponse>('/promo-codes', data);
    return response.data.data;
  }

  async updatePromoCode(id: string, data: any): Promise<any> {
    const response = await this.api.put<ApiResponse>(`/promo-codes/${id}`, data);
    return response.data.data;
  }

  async deletePromoCode(id: string): Promise<void> {
    await this.api.delete(`/promo-codes/${id}`);
  }

  async getPromoCodeStats(id: string): Promise<any> {
    const response = await this.api.get<ApiResponse>(`/promo-codes/${id}/stats`);
    return response.data.data;
  }

  // Referral Link endpoints
  async getReferralLinks(params?: any, signal?: AbortSignal): Promise<{ data: any[]; pagination: any }> {
    const response = await this.api.get<ApiResponse>('/referral-links', { params, signal });
    return {
      data: response.data.data || [],
      pagination: response.data.pagination
    };
  }

  async getReferralLink(id: string): Promise<any> {
    const response = await this.api.get<ApiResponse>(`/referral-links/${id}`);
    return response.data.data;
  }

  async createReferralLink(data: any): Promise<any> {
    const response = await this.api.post<ApiResponse>('/referral-links', data);
    return response.data.data;
  }

  async updateReferralLink(id: string, data: any): Promise<any> {
    const response = await this.api.put<ApiResponse>(`/referral-links/${id}`, data);
    return response.data.data;
  }

  async deleteReferralLink(id: string): Promise<void> {
    await this.api.delete(`/referral-links/${id}`);
  }

  async getReferralLinkStats(id: string): Promise<any> {
    const response = await this.api.get<ApiResponse>(`/referral-links/${id}/stats`);
    return response.data.data;
  }

  async trackReferralClick(code: string, clientId?: string): Promise<any> {
    const response = await this.api.post<ApiResponse>(`/referral-links/track/${code}`, { clientId });
    return response.data.data;
  }

  // Marketer endpoints (require admin auth, not marketer auth)
  async getMarketers(params?: any, signal?: AbortSignal): Promise<{ data: any[]; pagination: any }> {
    // Use regular token for admin endpoints
    const regularToken = localStorage.getItem('token');
    const marketerToken = localStorage.getItem('marketerToken');
    
    // Temporarily use regular token if available
    const originalAuth = this.api.defaults.headers.common['Authorization'];
    const originalAuthStr = typeof originalAuth === 'string' ? originalAuth : '';
    
    if (regularToken && !originalAuthStr.includes(regularToken)) {
      this.api.defaults.headers.common['Authorization'] = `Bearer ${regularToken}`;
    }
    
    try {
      const response = await this.api.get<ApiResponse>('/marketers', { params, signal });
      return {
        data: response.data.data || [],
        pagination: response.data.pagination
      };
    } finally {
      // Restore original auth
      if (marketerToken) {
        this.api.defaults.headers.common['Authorization'] = `Bearer ${marketerToken}`;
      } else if (regularToken) {
        this.api.defaults.headers.common['Authorization'] = `Bearer ${regularToken}`;
      } else {
        delete this.api.defaults.headers.common['Authorization'];
      }
    }
  }

  async getMarketer(id: string): Promise<any> {
    const response = await this.api.get<ApiResponse>(`/marketers/${id}`);
    return response.data.data;
  }

  async createMarketer(data: any): Promise<any> {
    const response = await this.api.post<ApiResponse>('/marketers', data);
    return response.data.data;
  }

  async updateMarketer(id: string, data: any): Promise<any> {
    const response = await this.api.put<ApiResponse>(`/marketers/${id}`, data);
    return response.data.data;
  }

  async deleteMarketer(id: string): Promise<void> {
    await this.api.delete(`/marketers/${id}`);
  }

  async getMarketerStats(id: string): Promise<MarketerStatsSummary> {
    // If id is 'me', use the special endpoint for authenticated marketer
    const endpoint = id === 'me' ? '/marketers/me/stats' : `/marketers/${id}/stats`;
    const response = await this.api.get<ApiResponse<MarketerStatsSummary>>(endpoint);
    return response.data.data!;
  }

  async getMarketerDashboard(): Promise<any> {
    const response = await this.api.get<ApiResponse>('/marketers/me/dashboard');
    return response.data.data;
  }

  async getMarketerClients(): Promise<{
    leads: any[];
    schools: any[];
    adLink?: { code: string; path: string } | null;
    personalPromo?: {
      code: string;
      discountType: string;
      discountValue: number;
      description?: string | null;
      usedCount?: number;
      usageLimit?: number | null;
      validUntil?: string | null;
    } | null;
  }> {
    const response = await this.api.get<ApiResponse>('/marketers/me/clients');
    return response.data.data || { leads: [], schools: [] };
  }

  async getMarketerClientCard(id: string): Promise<any> {
    const response = await this.api.get<ApiResponse>(`/marketers/me/clients/${id}`);
    return response.data.data;
  }

  async createMarketerLead(data: Record<string, unknown>): Promise<any> {
    const response = await this.api.post<ApiResponse>('/marketers/me/leads', data);
    return response.data.data;
  }

  async updateMarketerLead(id: string, data: Record<string, unknown>): Promise<any> {
    const response = await this.api.patch<ApiResponse>(`/marketers/me/leads/${id}`, data);
    return response.data.data;
  }

  async claimMarketerClient(code: string): Promise<any> {
    const response = await this.api.post<ApiResponse>('/marketers/me/clients/claim', { code });
    return response.data.data;
  }

  async getMarketerTasks(params?: Record<string, string>): Promise<any[]> {
    const response = await this.api.get<ApiResponse>('/marketers/me/tasks', { params });
    return response.data.data || [];
  }

  async createMarketerTask(data: Record<string, unknown>): Promise<any> {
    const response = await this.api.post<ApiResponse>('/marketers/me/tasks', data);
    return response.data.data;
  }

  async updateMarketerTask(id: string, data: Record<string, unknown>): Promise<any> {
    const response = await this.api.patch<ApiResponse>(`/marketers/me/tasks/${id}`, data);
    return response.data.data;
  }

  async getMarketerChats(): Promise<any[]> {
    const response = await this.api.get<ApiResponse>('/marketers/me/chats');
    return response.data.data || [];
  }

  async ensureMarketerChat(channel: 'SUPPORT' | 'ACCOUNTING'): Promise<any> {
    const response = await this.api.post<ApiResponse>(`/marketers/me/chats/${channel}/ensure`);
    return response.data.data;
  }

  async getMarketerChatMessages(threadId: string): Promise<any[]> {
    const response = await this.api.get<ApiResponse>(`/marketers/me/chats/${threadId}/messages`);
    return response.data.data || [];
  }

  async postMarketerChatMessage(threadId: string, body: string): Promise<any> {
    const response = await this.api.post<ApiResponse>(`/marketers/me/chats/${threadId}/messages`, {
      body,
    });
    return response.data.data;
  }

  async getMarketerFinance(): Promise<any> {
    const response = await this.api.get<ApiResponse>('/marketers/me/finance');
    return response.data.data;
  }

  async updateMarketerBankDetails(data: {
    bankName: string;
    bankBik: string;
    bankAccount: string;
    bankRecipient: string;
  }): Promise<any> {
    const response = await this.api.put<ApiResponse>('/marketers/me/bank-details', data);
    return response.data.data;
  }

  async createMarketerPayout(amount: number): Promise<any> {
    const response = await this.api.post<ApiResponse>('/marketers/me/payouts', { amount });
    return response.data.data;
  }

  async cancelMarketerPayout(id: string): Promise<any> {
    const response = await this.api.post<ApiResponse>(`/marketers/me/payouts/${id}/cancel`);
    return response.data.data;
  }

  async sendMarketerPayoutSignCode(id: string): Promise<any> {
    const response = await this.api.post<ApiResponse>(`/marketers/me/payouts/${id}/send-sign-code`);
    return response.data.data;
  }

  async confirmMarketerPayoutSign(id: string, code: string): Promise<any> {
    const response = await this.api.post<ApiResponse>(`/marketers/me/payouts/${id}/confirm-sign`, {
      code,
    });
    return response.data.data;
  }

  async downloadMarketerPayoutDocumentBlob(
    id: string,
    version: 'auto' | 'draft' | 'signed' = 'auto'
  ): Promise<Blob> {
    const response = await this.api.get(`/marketers/me/payouts/${id}/document`, {
      responseType: 'blob',
      params: version && version !== 'auto' ? { version } : undefined,
    });
    return response.data;
  }

  async uploadMarketerNpdReceipt(id: string, file: File): Promise<any> {
    const form = new FormData();
    form.append('file', file);
    const response = await this.api.post<ApiResponse>(`/marketers/me/payouts/${id}/npd-receipt`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data.data;
  }

  async downloadMarketerNpdReceiptBlob(id: string): Promise<Blob> {
    const response = await this.api.get(`/marketers/me/payouts/${id}/npd-receipt`, {
      responseType: 'blob',
    });
    return response.data;
  }

  async adminListMarketerPayouts(status?: string): Promise<any[]> {
    const response = await this.api.get<ApiResponse>('/admin-dashboard/marketer-payouts', {
      params: status ? { status } : undefined,
    });
    return response.data.data || [];
  }

  async adminMarkMarketerPayoutPaid(id: string, note?: string): Promise<any> {
    const response = await this.api.post<ApiResponse>(
      `/admin-dashboard/marketer-payouts/${id}/mark-paid`,
      { note }
    );
    return response.data.data;
  }

  async adminRejectMarketerPayout(id: string, reason?: string): Promise<any> {
    const response = await this.api.post<ApiResponse>(
      `/admin-dashboard/marketer-payouts/${id}/reject`,
      { reason }
    );
    return response.data.data;
  }

  async adminDownloadMarketerPayoutDocumentBlob(
    id: string,
    version: 'auto' | 'draft' | 'signed' = 'auto'
  ): Promise<Blob> {
    const response = await this.api.get(`/admin-dashboard/marketer-payouts/${id}/document`, {
      responseType: 'blob',
      params: version && version !== 'auto' ? { version } : undefined,
    });
    return response.data;
  }

  async adminDownloadMarketerNpdReceiptBlob(id: string): Promise<Blob> {
    const response = await this.api.get(`/admin-dashboard/marketer-payouts/${id}/npd-receipt`, {
      responseType: 'blob',
    });
    return response.data;
  }

  async getMarketerClosingDocs(): Promise<any[]> {
    const response = await this.api.get<ApiResponse>('/marketers/me/closing-docs');
    return response.data.data || [];
  }

  async adminListMarketerClosingDocs(): Promise<any[]> {
    const response = await this.api.get<ApiResponse>('/admin-dashboard/marketer-closing-docs');
    return response.data.data || [];
  }

  async adminCreateMarketerClosingDoc(data: {
    title: string;
    periodLabel?: string;
    marketerId?: string | null;
    file: File;
  }): Promise<any> {
    const form = new FormData();
    form.append('title', data.title);
    if (data.periodLabel) form.append('periodLabel', data.periodLabel);
    if (data.marketerId) form.append('marketerId', data.marketerId);
    form.append('file', data.file);
    const response = await this.api.post<ApiResponse>('/admin-dashboard/marketer-closing-docs', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data.data;
  }

  async adminListMarketerLegalDocs(): Promise<{
    packVersion: string;
    packUpdatedAt: string | null;
    placeholders?: string[];
    documents: Array<{
      key: string;
      title: string;
      fileName: string;
      sortOrder: number;
      onboarding: boolean;
      url: string;
      id: string | null;
      size: number | null;
      version: string | null;
      updatedAt: string | null;
      uploadedById: string | null;
      hasFile: boolean;
      hasBody?: boolean;
      bodyPreview?: string | null;
    }>;
  }> {
    const response = await this.api.get<ApiResponse>('/admin-dashboard/marketer-legal-docs');
    return response.data.data;
  }

  async adminGetMarketerLegalDocBody(key: string): Promise<{
    key: string;
    title: string;
    onboarding: boolean;
    bodyText: string;
    version: string | null;
    updatedAt: string | null;
    url: string;
  }> {
    const response = await this.api.get<ApiResponse>(
      `/admin-dashboard/marketer-legal-docs/${encodeURIComponent(key)}/body`
    );
    return response.data.data;
  }

  async adminUpdateMarketerLegalDocBody(
    key: string,
    bodyText: string
  ): Promise<{ packVersion: string; document: any; bodyText: string }> {
    const response = await this.api.put<ApiResponse>(
      `/admin-dashboard/marketer-legal-docs/${encodeURIComponent(key)}/body`,
      { bodyText }
    );
    return response.data.data;
  }

  async adminUploadMarketerLegalDoc(
    key: string,
    file: File
  ): Promise<{
    packVersion: string;
    document: {
      key: string;
      title: string;
      fileName: string;
      size: number;
      version: string;
      updatedAt: string;
      url: string;
      onboarding: boolean;
      hasFile: boolean;
    };
  }> {
    const form = new FormData();
    form.append('file', file);
    const response = await this.api.post<ApiResponse>(
      `/admin-dashboard/marketer-legal-docs/${encodeURIComponent(key)}`,
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data.data;
  }

  async adminGetMobileApk(): Promise<{
    hasFile: boolean;
    originalName: string | null;
    size: number | null;
    label: string | null;
    uploadedAt: string | null;
  }> {
    const response = await this.api.get<ApiResponse>('/admin-dashboard/mobile-apk');
    return response.data.data;
  }

  /**
   * Крупные multipart через CRA `/api` proxy → HTTP 413.
   * В локальной разработке бьём напрямую в backend :3001 (тот же hostname, что у UI).
   */
  private largeUploadBaseURL(): string {
    const base = getApiBaseUrl();
    if (base && base !== '/api') return base.replace(/\/$/, '');
    if (typeof window !== 'undefined') {
      const host = window.location.hostname;
      if (host === 'localhost' || host === '127.0.0.1') {
        return `${window.location.protocol}//${host}:3001/api`;
      }
    }
    return String(this.api.defaults.baseURL || '/api').replace(/\/$/, '');
  }

  async adminUploadMobileApk(
    file: File,
    label?: string
  ): Promise<{
    hasFile: boolean;
    originalName: string | null;
    size: number | null;
    label: string | null;
    uploadedAt: string | null;
  }> {
    const form = new FormData();
    form.append('file', file);
    if (label) form.append('label', label);
    const response = await this.api.post<ApiResponse>('/admin-dashboard/mobile-apk', form, {
      baseURL: this.largeUploadBaseURL(),
      timeout: 10 * 60 * 1000,
      maxBodyLength: Infinity,
      maxContentLength: Infinity,
    });
    return response.data.data;
  }

  async adminUpdateMobileApkLabel(label: string | null): Promise<{
    hasFile: boolean;
    originalName: string | null;
    size: number | null;
    label: string | null;
    uploadedAt: string | null;
  }> {
    const response = await this.api.put<ApiResponse>('/admin-dashboard/mobile-apk/label', {
      label,
    });
    return response.data.data;
  }

  async adminDeleteMobileApk(): Promise<{
    hasFile: boolean;
    originalName: string | null;
    size: number | null;
    label: string | null;
    uploadedAt: string | null;
  }> {
    const response = await this.api.delete<ApiResponse>('/admin-dashboard/mobile-apk');
    return response.data.data;
  }

  async adminDownloadMobileApk(): Promise<void> {
    // Не через axios blob: дефолтный timeout 10s и буфер всего APK в RAM.
    // Браузер качает потоком по прямой ссылке (Bearer из query только для этого файла).
    const token =
      (typeof localStorage !== 'undefined' && localStorage.getItem('superAdminToken')) || '';
    if (!token) {
      throw new Error('Нет токена супер-админа');
    }
    const base = this.largeUploadBaseURL().replace(/\/$/, '');
    const url = `${base}/admin-dashboard/mobile-apk/file?access_token=${encodeURIComponent(token)}`;
    const a = document.createElement('a');
    a.href = url;
    a.rel = 'noopener';
    a.download = 'profsport.apk';
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  async getPublicMarketerLegalDocs(): Promise<{
    packVersion: string;
    documents: Array<{
      key: string;
      title: string;
      version: string;
      url: string;
      onboarding: boolean;
    }>;
  }> {
    const response = await this.api.get<ApiResponse>('/legal/marketer');
    return response.data.data;
  }

  async downloadMarketerLegalDocPersonalBlob(key: string): Promise<Blob> {
    const response = await this.api.get(`/marketers/me/legal-docs/${encodeURIComponent(key)}`, {
      responseType: 'blob',
    });
    return response.data;
  }

  async downloadMarketerClosingDocBlob(id: string): Promise<Blob> {
    const response = await this.api.get(`/marketers/me/closing-docs/${id}/file`, {
      responseType: 'blob',
    });
    return response.data;
  }

  async adminDownloadMarketerClosingDocBlob(id: string): Promise<Blob> {
    const response = await this.api.get(`/admin-dashboard/marketer-closing-docs/${id}/file`, {
      responseType: 'blob',
    });
    return response.data;
  }

  // Promo Code Admin endpoints (require regular admin auth)
  async getPromoCodeAdmins(params?: any, signal?: AbortSignal): Promise<{ data: any[]; pagination: any }> {
    // Use regular token for admin endpoints
    const regularToken = localStorage.getItem('token');
    const promoCodeAdminToken = localStorage.getItem('promoCodeAdminToken');
    
    // Temporarily use regular token if available
    const originalAuth = this.api.defaults.headers.common['Authorization'];
    const originalAuthStr = typeof originalAuth === 'string' ? originalAuth : '';
    
    if (regularToken && !originalAuthStr.includes(regularToken)) {
      this.api.defaults.headers.common['Authorization'] = `Bearer ${regularToken}`;
    }
    
    try {
      const response = await this.api.get<ApiResponse>('/promo-code-admins', { params, signal });
      return {
        data: response.data.data || [],
        pagination: response.data.pagination
      };
    } finally {
      // Restore original auth
      if (promoCodeAdminToken) {
        this.api.defaults.headers.common['Authorization'] = `Bearer ${promoCodeAdminToken}`;
      } else if (regularToken) {
        this.api.defaults.headers.common['Authorization'] = `Bearer ${regularToken}`;
      } else {
        delete this.api.defaults.headers.common['Authorization'];
      }
    }
  }

  async getPromoCodeAdmin(id: string): Promise<any> {
    const response = await this.api.get<ApiResponse>(`/promo-code-admins/${id}`);
    return response.data.data;
  }

  async createPromoCodeAdmin(data: any): Promise<any> {
    const response = await this.api.post<ApiResponse>('/promo-code-admins', data);
    return response.data.data;
  }

  async updatePromoCodeAdmin(id: string, data: any): Promise<any> {
    const response = await this.api.put<ApiResponse>(`/promo-code-admins/${id}`, data);
    return response.data.data;
  }

  async deletePromoCodeAdmin(id: string): Promise<void> {
    await this.api.delete(`/promo-code-admins/${id}`);
  }

  // Settings
  async getSettings(): Promise<any> {
    const response = await this.api.get<ApiResponse>('/settings');
    return response.data;
  }

  async updateSettings(data: any): Promise<any> {
    const response = await this.api.put<ApiResponse>('/settings', data);
    return response.data;
  }

  async updateOnboardingStatus(data: { hasCompletedOnboarding?: boolean; onboardingDeclined?: boolean }): Promise<any> {
    const response = await this.api.post<ApiResponse>('/settings/onboarding', data);
    return response.data;
  }

  async approveParentAccount(parentId: string): Promise<any> {
    const response = await this.api.put<ApiResponse>(`/clients/parents/${parentId}/approve`);
    return response.data;
  }

  async resetMembershipFees(): Promise<any> {
    const response = await this.api.post<ApiResponse>('/settings/reset-membership-fees');
    return response.data;
  }


  // Subscription methods
  async getSubscription(): Promise<any> {
    const response = await this.api.get<ApiResponse>('/subscriptions');
    return response.data.data;
  }

  async validatePromoCode(promoCode: string, planType: string): Promise<any> {
    const response = await this.api.post<ApiResponse>('/subscriptions/validate-promo-code', {
      promoCode,
      planType,
    });
    return response.data.data;
  }

  async createSubscriptionPayment(planType: string, returnUrl?: string, promoCode?: string): Promise<any> {
    const refCode = getStoredRefCode();
    const response = await this.api.post<ApiResponse>('/subscriptions/payment', {
      planType,
      returnUrl,
      promoCode,
      refCode,
    });
    return response.data.data;
  }

  async registerMarketer(data: {
    name: string;
    email: string;
    password: string;
    phone?: string;
    type?: string;
    inviteToken?: string;
    ref?: string;
  }): Promise<any> {
    const response = await this.api.post<ApiResponse>('/auth/marketer/register', data);
    return response.data.data;
  }

  async getMarketerTeam(): Promise<any> {
    const response = await this.api.get<ApiResponse>('/marketers/me/team');
    return response.data.data;
  }

  async getMarketerActivity(): Promise<any> {
    const response = await this.api.get<ApiResponse>('/marketers/me/activity');
    return response.data.data;
  }

  async getMarketerProfile(): Promise<any> {
    const response = await this.api.get<ApiResponse>('/marketers/me/profile');
    return response.data.data;
  }

  async changeMarketerPassword(data: {
    currentPassword: string;
    newPassword: string;
  }): Promise<{ message?: string }> {
    const response = await this.api.post<ApiResponse>('/marketers/me/change-password', data);
    return response.data.data || { message: 'Пароль успешно изменён' };
  }

  async getMarketerNotificationPrefs(): Promise<NotificationPrefs> {
    const response = await this.api.get<ApiResponse>('/marketers/me/notification-prefs');
    return response.data.data;
  }

  async updateMarketerNotificationPrefs(
    data: Partial<NotificationPrefs>
  ): Promise<NotificationPrefs> {
    const response = await this.api.put<ApiResponse>('/marketers/me/notification-prefs', data);
    return response.data.data;
  }

  async updateMarketerProfile(data: {
    firstName?: string;
    lastName?: string;
    phone?: string | null;
    inn?: string | null;
    taxStatus?: string;
    confirmNpd?: boolean;
  }): Promise<any> {
    const response = await this.api.patch<ApiResponse>('/marketers/me/profile', data);
    return response.data.data;
  }

  async getMarketerOnboarding(): Promise<any> {
    const response = await this.api.get<ApiResponse>('/marketers/me/onboarding');
    return response.data.data;
  }

  async acceptMarketerOnboarding(data: {
    offer?: boolean;
    pep?: boolean;
    pdn?: boolean;
    programRules?: boolean;
  }): Promise<any> {
    const response = await this.api.post<ApiResponse>('/marketers/me/onboarding/accept', data);
    return response.data.data;
  }

  async getMarketerInvite(): Promise<any> {
    const response = await this.api.get<ApiResponse>('/marketers/me/invite');
    return response.data.data;
  }

  async resolveMarketerInvite(token: string): Promise<any> {
    const response = await this.api.get<ApiResponse>(`/marketers/invite/${encodeURIComponent(token)}`);
    return response.data.data;
  }

  async getPlatformMarketers(params?: any): Promise<{ data: any[]; pagination: any }> {
    const response = await this.api.get<ApiResponse>('/admin-dashboard/platform/marketers', { params });
    return { data: response.data.data || [], pagination: response.data.pagination };
  }

  async getPlatformMarketer(id: string): Promise<any> {
    const response = await this.api.get<ApiResponse>(`/admin-dashboard/platform/marketers/${id}`);
    return response.data.data;
  }

  /** Клики/промо-статы по маркетологу (platform/:id/stats). */
  async getPlatformMarketerDetailStats(id: string): Promise<any> {
    const response = await this.api.get<ApiResponse>(`/admin-dashboard/platform/marketers/${id}/stats`);
    return response.data.data;
  }

  async createPlatformMarketer(data: any): Promise<any> {
    const response = await this.api.post<ApiResponse>('/admin-dashboard/platform/marketers', data);
    return response.data.data;
  }

  async updatePlatformMarketer(id: string, data: any): Promise<any> {
    const response = await this.api.put<ApiResponse>(`/admin-dashboard/platform/marketers/${id}`, data);
    return response.data.data;
  }

  async deletePlatformMarketer(id: string): Promise<void> {
    await this.api.delete(`/admin-dashboard/platform/marketers/${id}`);
  }

  async getPlatformPromoCodes(params?: any): Promise<{ data: any[]; pagination: any }> {
    const response = await this.api.get<ApiResponse>('/admin-dashboard/platform/promo-codes', { params });
    return { data: response.data.data || [], pagination: response.data.pagination };
  }

  async createPlatformPromoCode(data: any): Promise<any> {
    const response = await this.api.post<ApiResponse>('/admin-dashboard/platform/promo-codes', data);
    return response.data.data;
  }

  async updatePlatformPromoCode(id: string, data: any): Promise<any> {
    const response = await this.api.put<ApiResponse>(`/admin-dashboard/platform/promo-codes/${id}`, data);
    return response.data.data;
  }

  async deletePlatformPromoCode(id: string): Promise<void> {
    await this.api.delete(`/admin-dashboard/platform/promo-codes/${id}`);
  }

  async getPlatformReferralLinks(params?: any): Promise<{ data: any[]; pagination: any }> {
    const response = await this.api.get<ApiResponse>('/admin-dashboard/platform/referral-links', { params });
    return { data: response.data.data || [], pagination: response.data.pagination };
  }

  async createPlatformReferralLink(data: any): Promise<any> {
    const response = await this.api.post<ApiResponse>('/admin-dashboard/platform/referral-links', data);
    return response.data.data;
  }

  async updatePlatformReferralLink(id: string, data: any): Promise<any> {
    const response = await this.api.put<ApiResponse>(`/admin-dashboard/platform/referral-links/${id}`, data);
    return response.data.data;
  }

  async deletePlatformReferralLink(id: string): Promise<void> {
    await this.api.delete(`/admin-dashboard/platform/referral-links/${id}`);
  }

  async updateSubscriptionPlan(planType: string): Promise<any> {
    const response = await this.api.put<ApiResponse>('/subscriptions/plan', {
      planType,
    });
    return response.data.data;
  }

  async checkResourceLimit(resource: string): Promise<any> {
    const response = await this.api.get<ApiResponse>(`/subscriptions/check-limit?resource=${resource}`);
    return response.data.data;
  }

  async getPlanUsage(): Promise<any> {
    const response = await this.api.get<ApiResponse>('/subscriptions/plan-usage');
    return response.data.data;
  }

  // Admin Dashboard methods
  async getAdminDashboard(): Promise<any> {
    const response = await this.api.get<ApiResponse>('/admin-dashboard');
    return response.data.data;
  }

  async updateAdminSettings(data: {
    reservePercentage?: number;
    reserveAmount?: number;
    /** Абсолютный путь к файлу ошибок сервера. */
    errorLogPath?: string | null;
  }): Promise<any> {
    const response = await this.api.put<ApiResponse>('/admin-dashboard/settings', data);
    return response.data.data;
  }

  async getTenantDetails(tenantId: string): Promise<any> {
    const response = await this.api.get<ApiResponse>(`/admin-dashboard/tenants/${tenantId}`);
    return response.data.data;
  }

  // Super Admin Auth methods
  async superAdminLogin(data: { email: string; password: string }): Promise<any> {
    const response = await this.api.post<ApiResponse>('/super-admin/auth/login', data);
    return response.data.data;
  }

  // Set token manually (for super admin)
  setToken(token: string | null): void {
    if (token) {
      localStorage.setItem('superAdminToken', token);
    } else {
      localStorage.removeItem('superAdminToken');
    }
  }

  // Admin Dashboard additional methods
  async getAllTenants(): Promise<any> {
    const response = await this.api.get<ApiResponse>('/admin-dashboard/tenants');
    return response.data.data;
  }

  async getTransactionHistory(params?: { type?: string; limit?: number; offset?: number; startDate?: string; endDate?: string; categoryId?: string }): Promise<any> {
    const response = await this.api.get<ApiResponse>('/admin-dashboard/transactions', { params });
    return response.data.data;
  }

  async createExpense(data: {
    amount: number;
    description: string;
    categoryId?: string;
    documentUrl?: string;
    isRecurring?: boolean;
    recurringPeriod?: string;
    nextDueDate?: string;
  }): Promise<any> {
    const response = await this.api.post<ApiResponse>('/admin-dashboard/expenses', data);
    return response.data.data;
  }

  async updateExpense(id: string, data: {
    amount?: number;
    description?: string;
    categoryId?: string;
    documentUrl?: string;
    isRecurring?: boolean;
    recurringPeriod?: string;
    nextDueDate?: string;
  }): Promise<any> {
    const response = await this.api.put<ApiResponse>(`/admin-dashboard/expenses/${id}`, data);
    return response.data.data;
  }

  async deleteExpense(id: string): Promise<void> {
    await this.api.delete(`/admin-dashboard/expenses/${id}`);
  }

  async getExpenseCategories(): Promise<any> {
    const response = await this.api.get<ApiResponse>('/admin-dashboard/expense-categories');
    return response.data.data;
  }

  async createExpenseCategory(data: { name: string; description?: string; color?: string }): Promise<any> {
    const response = await this.api.post<ApiResponse>('/admin-dashboard/expense-categories', data);
    return response.data.data;
  }

  async getAnalyticsByPeriod(params?: {
    period?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<any> {
    const response = await this.api.get<ApiResponse>('/admin-dashboard/analytics/period', { params });
    return response.data.data;
  }

  async getRevenueForecast(): Promise<any> {
    const response = await this.api.get<ApiResponse>('/admin-dashboard/analytics/forecast');
    return response.data.data;
  }

  async getKPIMetrics(period?: string): Promise<any> {
    const response = await this.api.get<ApiResponse>('/admin-dashboard/analytics/kpi', {
      params: { period },
    });
    return response.data.data;
  }

  async getAuditLogs(params?: {
    limit?: number;
    offset?: number;
    action?: string;
    entityType?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<any> {
    const response = await this.api.get<ApiResponse>('/admin-dashboard/audit-logs', { params });
    return response.data.data;
  }

  async getBudgetLimits(): Promise<any> {
    const response = await this.api.get<ApiResponse>('/admin-dashboard/budget/limits');
    return response.data.data;
  }

  async setBudgetLimit(data: {
    categoryId?: string;
    categoryName?: string;
    limitAmount: number;
    period: string;
  }): Promise<any> {
    const response = await this.api.post<ApiResponse>('/admin-dashboard/budget/limits', data);
    return response.data.data;
  }

  /* ---------------- Каталог тарифов ---------------- */

  /**
   * Список тарифов для панели супер-админа (включая архивные и индивидуальные).
   * Персоналу платформы доступен только на чтение.
   */
  async getPlanPrices(): Promise<SubscriptionPlanItem[]> {
    const response = await this.api.get<ApiResponse<SubscriptionPlanItem[]>>(
      '/admin-dashboard/plans/prices'
    );
    return response.data.data!;
  }

  /** Создание тарифа. `isPublic: false` — индивидуальный тариф. */
  async createPlan(data: {
    code: string;
    name: string;
    description?: string | null;
    price?: number | null;
    limits?: Record<string, number | 'unlimited'>;
    isPublic?: boolean;
    sortOrder?: number;
    supportLevel?: string | null;
  }): Promise<SubscriptionPlanItem> {
    const response = await this.api.post<ApiResponse<SubscriptionPlanItem>>(
      '/admin-dashboard/plans',
      data
    );
    return response.data.data!;
  }

  /** Изменение тарифа: название, цена, лимиты, публичность. Код неизменяем. */
  async updatePlan(
    code: string,
    data: {
      name?: string;
      description?: string | null;
      price?: number | null;
      limits?: Record<string, number | 'unlimited'>;
      isPublic?: boolean;
      isActive?: boolean;
      sortOrder?: number;
      supportLevel?: string | null;
    }
  ): Promise<SubscriptionPlanItem> {
    const response = await this.api.put<ApiResponse<SubscriptionPlanItem>>(
      `/admin-dashboard/plans/${encodeURIComponent(code)}`,
      data
    );
    return response.data.data!;
  }

  /** Архивация тарифа (мягкое удаление — история сохраняется). */
  async archivePlan(code: string): Promise<SubscriptionPlanItem> {
    const response = await this.api.post<ApiResponse<SubscriptionPlanItem>>(
      `/admin-dashboard/plans/${encodeURIComponent(code)}/archive`,
      {}
    );
    return response.data.data!;
  }

  /** Восстановление тарифа из архива. */
  async restorePlan(code: string): Promise<SubscriptionPlanItem> {
    const response = await this.api.post<ApiResponse<SubscriptionPlanItem>>(
      `/admin-dashboard/plans/${encodeURIComponent(code)}/restore`,
      {}
    );
    return response.data.data!;
  }

  /** Публичный каталог тарифов для страницы /pricing (без индивидуальных). */
  async getPublicPlans(): Promise<PublicPlanItem[]> {
    const response = await this.api.get<ApiResponse<PublicPlanItem[]>>('/subscriptions/plans');
    return response.data.data!;
  }

  /** Совместимость: обновление цены и лимитов существующего тарифа. */
  async updatePlanPrice(data: { planType: string; price?: number | null; limits?: any }): Promise<any> {
    const response = await this.api.put<ApiResponse>('/admin-dashboard/plans/prices', data);
    return response.data.data;
  }

  async getAdminMarketerStats(marketerId?: string): Promise<any> {
    const response = await this.api.get<ApiResponse>('/admin-dashboard/marketers/stats', {
      params: marketerId ? { marketerId } : {},
    });
    return response.data.data;
  }

  async bulkUpdateTenants(data: {
    tenantIds: string[];
    action: string;
    data?: any;
  }): Promise<any> {
    const response = await this.api.post<ApiResponse>('/admin-dashboard/tenants/bulk', data);
    return response.data;
  }

  async getTenantDeletionImpact(tenantId: string): Promise<Record<string, number>> {
    const response = await this.api.get<ApiResponse>(
      `/admin-dashboard/tenants/${tenantId}/deletion-impact`
    );
    return response.data.data || {};
  }

  /** Полное необратимое удаление школы (подтверждение — поддомен). */
  async deleteTenant(
    tenantId: string,
    confirmSubdomain: string,
    purge?: Record<string, boolean>
  ): Promise<{
    tenantId: string;
    name: string;
    subdomain: string;
    email: string;
    clients: number;
    users: number;
    payments: number;
  }> {
    const response = await this.api.delete<ApiResponse>(`/admin-dashboard/tenants/${tenantId}`, {
      data: { confirmSubdomain, purge: purge || {} },
    });
    return response.data.data;
  }

  /**
   * Выдача тарифа аккаунту. Применяется сразу, независимо от уровня тарифа.
   * `endDate` — необязательный срок действия, `comment` попадает в историю.
   */
  async updateTenantPlan(
    tenantId: string,
    planType: string,
    options?: { endDate?: string | null; comment?: string | null }
  ): Promise<any> {
    const response = await this.api.put<ApiResponse>(`/admin-dashboard/tenants/${tenantId}/plan`, {
      planType,
      ...(options?.endDate ? { endDate: options.endDate } : {}),
      ...(options?.comment ? { comment: options.comment } : {}),
    });
    return response.data.data;
  }

  /** Изменение срока действия тарифа аккаунта с записью в историю. */
  async updateTenantSubscriptionEndDate(
    tenantId: string,
    endDate: string,
    comment?: string | null
  ): Promise<any> {
    const response = await this.api.put<ApiResponse>(
      `/admin-dashboard/tenants/${tenantId}/subscription/end-date`,
      { endDate, ...(comment ? { comment } : {}) }
    );
    return response.data.data;
  }

  /** История выдачи и продления тарифов аккаунта. */
  async getTenantGrantHistory(tenantId: string): Promise<SubscriptionGrantLogItem[]> {
    const response = await this.api.get<ApiResponse<SubscriptionGrantLogItem[]>>(
      `/admin-dashboard/tenants/${tenantId}/grant-history`
    );
    return response.data.data!;
  }

  /* ---------------- Файл ошибок сервера ---------------- */

  /** Сведения о файле логов: путь, размер, дата изменения. */
  async getLogFileInfo(): Promise<LogFileInfoResponse> {
    const response = await this.api.get<ApiResponse<LogFileInfoResponse>>(
      '/admin-dashboard/logs/error-file'
    );
    return response.data.data!;
  }

  /** Последние строки файла логов. */
  async readLogFile(lines = 500): Promise<LogFileContentResponse> {
    const response = await this.api.get<ApiResponse<LogFileContentResponse>>(
      '/admin-dashboard/logs/error-file/content',
      { params: { lines } }
    );
    return response.data.data!;
  }

  /** Выгрузка файла логов. */
  async downloadLogFile(): Promise<Blob> {
    const response = await this.api.get('/admin-dashboard/logs/error-file/download', {
      responseType: 'blob',
    });
    return response.data as Blob;
  }

  /** Очистка файла логов. */
  async clearLogFile(): Promise<{ path: string; clearedBytes: number }> {
    const response = await this.api.delete<ApiResponse<{ path: string; clearedBytes: number }>>(
      '/admin-dashboard/logs/error-file'
    );
    return response.data.data!;
  }

  async payMarketer(data: { marketerId: string; amount: number; description?: string }): Promise<any> {
    const response = await this.api.post<ApiResponse>('/admin-dashboard/marketers/pay', data);
    return response.data.data;
  }

  async exportTransactions(params?: {
    type?: string;
    startDate?: string;
    endDate?: string;
    categoryId?: string;
  }): Promise<Blob> {
    const response = await this.api.get('/admin-dashboard/export/transactions', {
      params,
      responseType: 'blob',
    });
    return response.data;
  }

  async exportTenants(): Promise<Blob> {
    const response = await this.api.get('/admin-dashboard/export/tenants', {
      responseType: 'blob',
    });
    return response.data;
  }

  async downloadSchoolImportTemplate(): Promise<Blob> {
    const response = await this.api.get('/admin-dashboard/school-import/template', {
      responseType: 'blob',
    });
    return response.data;
  }

  async importSchoolExcel(
    tenantId: string,
    file: File
  ): Promise<{
    success: boolean;
    message?: string;
    error?: string;
    data?: {
      tenantId: string;
      tenantName: string;
      created: Record<string, number>;
      errors: Array<{ sheet: string; row: number; message: string }>;
      staffPasswords: Array<{ email: string; password: string; role: string }>;
    };
  }> {
    const form = new FormData();
    form.append('file', file);
    const response = await this.api.post(`/admin-dashboard/school-import/${tenantId}`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 180000,
      validateStatus: (s) => s < 500,
    });
    return response.data;
  }

  async exportMarketers(): Promise<Blob> {
    const response = await this.api.get('/admin-dashboard/export/marketers', {
      responseType: 'blob',
    });
    return response.data;
  }

  async getDashboardPresets(): Promise<any> {
    const response = await this.api.get<ApiResponse>('/admin-dashboard/dashboard/presets');
    return response.data.data;
  }

  async getDevNotes(params?: { status?: string; bucket?: string }): Promise<any[]> {
    const response = await this.api.get<ApiResponse>('/admin-dashboard/dev-notes', { params });
    return response.data.data || [];
  }

  async getDevNotesUnreadCount(): Promise<number> {
    const response = await this.api.get<ApiResponse>('/admin-dashboard/dev-notes/unread-count');
    return Number(response.data.data?.count || 0);
  }

  async createDevNote(data: {
    title: string;
    description?: string | null;
    status?: string;
    priority?: string | null;
    dueFrom?: string | null;
    dueTo?: string | null;
  }): Promise<any> {
    const response = await this.api.post<ApiResponse>('/admin-dashboard/dev-notes', data);
    return response.data.data;
  }

  async updateDevNote(
    id: string,
    data: {
      title?: string;
      description?: string | null;
      status?: string;
      priority?: string | null;
      dueFrom?: string | null;
      dueTo?: string | null;
    }
  ): Promise<any> {
    const response = await this.api.put<ApiResponse>(`/admin-dashboard/dev-notes/${id}`, data);
    return response.data.data;
  }

  async deleteDevNote(id: string): Promise<void> {
    await this.api.delete(`/admin-dashboard/dev-notes/${id}`);
  }

  async restoreDevNote(id: string): Promise<any> {
    const response = await this.api.post<ApiResponse>(`/admin-dashboard/dev-notes/${id}/restore`);
    return response.data.data;
  }

  async markDevNoteRead(id: string): Promise<any> {
    const response = await this.api.post<ApiResponse>(`/admin-dashboard/dev-notes/${id}/read`);
    return response.data.data;
  }

  async addDevNoteComment(id: string, body: string): Promise<any> {
    const response = await this.api.post<ApiResponse>(`/admin-dashboard/dev-notes/${id}/comments`, { body });
    return response.data.data;
  }

  async getSaNotifications(): Promise<any[]> {
    const response = await this.api.get<ApiResponse>('/admin-dashboard/sa-notifications');
    return response.data.data || [];
  }

  async markSaNotificationsRead(): Promise<void> {
    await this.api.post('/admin-dashboard/sa-notifications/read');
  }

  async getPlannedExpenses(): Promise<any[]> {
    const response = await this.api.get<ApiResponse>('/admin-dashboard/planned-expenses');
    return response.data.data || [];
  }

  async createPlannedExpense(data: {
    title: string;
    amount: number;
    dueDate: string;
    payeeKind: 'MARKETER' | 'OTHER';
    payeeName?: string | null;
    marketerId?: string | null;
    comment?: string | null;
  }): Promise<any> {
    const response = await this.api.post<ApiResponse>('/admin-dashboard/planned-expenses', data);
    return response.data.data;
  }

  async updatePlannedExpense(id: string, data: Record<string, unknown>): Promise<any> {
    const response = await this.api.put<ApiResponse>(`/admin-dashboard/planned-expenses/${id}`, data);
    return response.data.data;
  }

  async deletePlannedExpense(id: string): Promise<void> {
    await this.api.delete(`/admin-dashboard/planned-expenses/${id}`);
  }

  async listTestSessions(): Promise<{ roles: string[]; sessions: any[] }> {
    const response = await this.api.get<ApiResponse>('/admin-dashboard/test-sessions');
    return response.data.data || { roles: [], sessions: [] };
  }

  async enterTestSession(body: { role?: string; sessionId?: string }): Promise<any> {
    const response = await this.api.post<ApiResponse>('/admin-dashboard/test-sessions/enter', body);
    return response.data.data;
  }

  async saveTestSession(id: string, superAdminToken: string): Promise<void> {
    await this.api.post(`/admin-dashboard/test-sessions/${id}/save`, {}, {
      headers: { Authorization: `Bearer ${superAdminToken}` },
    });
  }

  async deleteTestSession(id: string, superAdminToken?: string): Promise<void> {
    await this.api.delete(`/admin-dashboard/test-sessions/${id}`, {
      headers: superAdminToken ? { Authorization: `Bearer ${superAdminToken}` } : undefined,
    });
  }

  async uploadDevNoteAttachments(noteId: string, files: File[]): Promise<any[]> {
    const formData = new FormData();
    files.forEach((f) => formData.append('files', f));
    const response = await this.api.post<ApiResponse>(
      `/admin-dashboard/dev-notes/${noteId}/attachments`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      }
    );
    return response.data.data || [];
  }

  async downloadDevNoteAttachment(
    noteId: string,
    attachmentId: string
  ): Promise<{ blob: Blob; filename: string }> {
    const response = await this.api.get(
      `/admin-dashboard/dev-notes/${noteId}/attachments/${attachmentId}/download`,
      { responseType: 'blob', timeout: 120000 }
    );
    const disposition = response.headers['content-disposition'] as string | undefined;
    const filename = parseContentDispositionFilename(disposition, 'file');
    return { blob: response.data, filename };
  }

  async deleteDevNoteAttachment(noteId: string, attachmentId: string): Promise<void> {
    await this.api.delete(`/admin-dashboard/dev-notes/${noteId}/attachments/${attachmentId}`);
  }

  async listClientContracts(clientId: string): Promise<any[]> {
    const response = await this.api.get<ApiResponse>(`/clients/${clientId}/contracts`);
    return response.data.data || [];
  }

  async uploadClientContract(
    clientId: string,
    file: File,
    meta?: { title?: string; signedAt?: string }
  ): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    if (meta?.title) formData.append('title', meta.title);
    if (meta?.signedAt) formData.append('signedAt', meta.signedAt);
    const response = await this.api.post<ApiResponse>(`/clients/${clientId}/contracts`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    });
    return response.data.data;
  }

  async downloadClientContract(
    clientId: string,
    contractId: string
  ): Promise<{ blob: Blob; filename: string }> {
    const response = await this.api.get(`/clients/${clientId}/contracts/${contractId}/download`, {
      responseType: 'blob',
      timeout: 120000,
    });
    const disposition = response.headers['content-disposition'] as string | undefined;
    const filename = parseContentDispositionFilename(disposition, 'contract.pdf');
    return { blob: response.data, filename };
  }

  async deleteClientContract(clientId: string, contractId: string): Promise<void> {
    await this.api.delete(`/clients/${clientId}/contracts/${contractId}`);
  }

  async uploadClientContractAddendum(
    clientId: string,
    contractId: string,
    file: File,
    meta?: { title?: string; signedAt?: string }
  ): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    if (meta?.title) formData.append('title', meta.title);
    if (meta?.signedAt) formData.append('signedAt', meta.signedAt);
    const response = await this.api.post<ApiResponse>(
      `/clients/${clientId}/contracts/${contractId}/addenda`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      }
    );
    return response.data.data;
  }

  async downloadClientContractAddendum(
    clientId: string,
    contractId: string,
    addendumId: string
  ): Promise<{ blob: Blob; filename: string }> {
    const response = await this.api.get(
      `/clients/${clientId}/contracts/${contractId}/addenda/${addendumId}/download`,
      { responseType: 'blob', timeout: 120000 }
    );
    const disposition = response.headers['content-disposition'] as string | undefined;
    const filename = parseContentDispositionFilename(disposition, 'addendum.pdf');
    return { blob: response.data, filename };
  }

  async deleteClientContractAddendum(
    clientId: string,
    contractId: string,
    addendumId: string
  ): Promise<void> {
    await this.api.delete(
      `/clients/${clientId}/contracts/${contractId}/addenda/${addendumId}`
    );
  }

  async clientListDocuments(clientId: string): Promise<{
    contracts: any[];
    personalDocs: {
      birthCertificate: boolean;
      birthCertificateNumber: string | null;
      medicalCertificate: boolean;
      medicalCertificateNumber: string | null;
    };
  }> {
    const response = await this.api.get<ApiResponse>(
      `/client-auth/clients/${clientId}/contracts`
    );
    return response.data.data;
  }

  async clientUploadContract(
    clientId: string,
    file: File,
    meta?: { title?: string }
  ): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    if (meta?.title) formData.append('title', meta.title);
    const response = await this.api.post<ApiResponse>(
      `/client-auth/clients/${clientId}/contracts`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 }
    );
    return response.data.data;
  }

  async clientUploadCertificate(
    clientId: string,
    kind: 'birth' | 'medical',
    file: File
  ): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await this.api.post<ApiResponse>(
      `/client-auth/clients/${clientId}/certificates/${kind}`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 }
    );
    return response.data.data;
  }

  async downloadClientCertificate(
    clientId: string,
    kind: 'birth' | 'medical'
  ): Promise<{ blob: Blob; filename: string }> {
    const response = await this.api.get(`/clients/${clientId}/certificates/${kind}/download`, {
      responseType: 'blob',
      timeout: 120000,
    });
    const disposition = response.headers['content-disposition'] as string | undefined;
    const fallback = kind === 'birth' ? 'birth-certificate' : 'medical-certificate';
    const filename = parseContentDispositionFilename(disposition, fallback);
    return { blob: response.data, filename };
  }

  async clientDownloadCertificate(
    clientId: string,
    kind: 'birth' | 'medical'
  ): Promise<{ blob: Blob; filename: string }> {
    const response = await this.api.get(
      `/client-auth/clients/${clientId}/certificates/${kind}/download`,
      { responseType: 'blob', timeout: 120000 }
    );
    const disposition = response.headers['content-disposition'] as string | undefined;
    const fallback = kind === 'birth' ? 'birth-certificate' : 'medical-certificate';
    const filename = parseContentDispositionFilename(disposition, fallback);
    return { blob: response.data, filename };
  }

  async clientDownloadContract(
    clientId: string,
    contractId: string
  ): Promise<{ blob: Blob; filename: string }> {
    const response = await this.api.get(
      `/client-auth/clients/${clientId}/contracts/${contractId}/download`,
      { responseType: 'blob', timeout: 120000 }
    );
    const disposition = response.headers['content-disposition'] as string | undefined;
    const filename = parseContentDispositionFilename(disposition, 'contract.pdf');
    return { blob: response.data, filename };
  }

  async clientDownloadContractAddendum(
    clientId: string,
    contractId: string,
    addendumId: string
  ): Promise<{ blob: Blob; filename: string }> {
    const response = await this.api.get(
      `/client-auth/clients/${clientId}/contracts/${contractId}/addenda/${addendumId}/download`,
      { responseType: 'blob', timeout: 120000 }
    );
    const disposition = response.headers['content-disposition'] as string | undefined;
    const filename = parseContentDispositionFilename(disposition, 'addendum.pdf');
    return { blob: response.data, filename };
  }

  async getPlannerEvents(params: { from: string; to: string }): Promise<{
    from: string;
    to: string;
    occurrences: Array<{
      occurrenceAt: string;
      event: {
        id: string;
        title: string;
        notes?: string | null;
        startAt: string;
        allDay: boolean;
        intervalUnit: 'NONE' | 'DAY' | 'WEEK' | 'MONTH' | 'YEAR';
        intervalCount: number;
        seriesEndAt?: string | null;
        createdBy?: { id: string; firstName: string; lastName: string; email: string };
      };
    }>;
    series: any[];
  }> {
    const response = await this.api.get<ApiResponse>('/admin-dashboard/planner/events', { params });
    return response.data.data;
  }

  async createPlannerEvent(data: {
    title: string;
    notes?: string | null;
    startAt: string;
    allDay?: boolean;
    intervalUnit?: string;
    intervalCount?: number;
    seriesEndAt?: string | null;
  }): Promise<any> {
    const response = await this.api.post<ApiResponse>('/admin-dashboard/planner/events', data);
    return response.data.data;
  }

  async updatePlannerEvent(
    id: string,
    data: {
      title?: string;
      notes?: string | null;
      startAt?: string;
      allDay?: boolean;
      intervalUnit?: string;
      intervalCount?: number;
      seriesEndAt?: string | null;
    }
  ): Promise<any> {
    const response = await this.api.put<ApiResponse>(`/admin-dashboard/planner/events/${id}`, data);
    return response.data.data;
  }

  async deletePlannerEvent(id: string): Promise<void> {
    await this.api.delete(`/admin-dashboard/planner/events/${id}`);
  }

  async linkTenantOwnerAsSuperAdmin(tenantId: string): Promise<any> {
    const response = await this.api.post<ApiResponse>(
      `/admin-dashboard/tenants/${tenantId}/link-super-admin`
    );
    return response.data.data;
  }

  async unlinkTenantOwnerSuperAdmin(tenantId: string): Promise<any> {
    const response = await this.api.delete<ApiResponse>(
      `/admin-dashboard/tenants/${tenantId}/link-super-admin`
    );
    return response.data.data;
  }

  async linkTenantOwnerAsTester(tenantId: string): Promise<any> {
    const response = await this.api.post<ApiResponse>(
      `/admin-dashboard/tenants/${tenantId}/link-tester`
    );
    return response.data.data;
  }

  async unlinkTenantOwnerTester(tenantId: string): Promise<any> {
    const response = await this.api.delete<ApiResponse>(
      `/admin-dashboard/tenants/${tenantId}/link-tester`
    );
    return response.data.data;
  }

  async saveDashboardPreset(data: {
    id?: string;
    name: string;
    isDefault?: boolean;
    widgetOrder: string[];
    widgetVisibility: Record<string, boolean>;
  }): Promise<any> {
    const response = await this.api.post<ApiResponse>('/admin-dashboard/dashboard/presets', data);
    return response.data.data;
  }

  async deleteDashboardPreset(id: string): Promise<void> {
    await this.api.delete(`/admin-dashboard/dashboard/presets/${id}`);
  }

  // Server metrics (super-admin)
  async getServerMetricsLive(): Promise<any> {
    const response = await this.api.get<ApiResponse>('/admin-dashboard/server-metrics/live');
    return response.data.data;
  }

  async getSiteTrafficLive(): Promise<any> {
    const response = await this.api.get<ApiResponse>('/admin-dashboard/site-traffic/live');
    return response.data.data;
  }

  async getSiteTrafficSummary(range: 'day' | 'month' | 'quarter' | 'year' = 'day'): Promise<any> {
    const response = await this.api.get<ApiResponse>('/admin-dashboard/site-traffic/summary', {
      params: { range },
    });
    return response.data.data;
  }

  async getSiteTrafficQuietHours(days = 30): Promise<any> {
    const response = await this.api.get<ApiResponse>('/admin-dashboard/site-traffic/quiet-hours', {
      params: { days },
    });
    return response.data.data;
  }

  async getServerMetricsHistory(range: '1h' | '6h' | '24h' | '7d' | '30d' = '1h'): Promise<any> {
    const response = await this.api.get<ApiResponse>('/admin-dashboard/server-metrics/history', {
      params: { range },
    });
    return response.data.data;
  }

  async getServerAlertSettings(): Promise<any> {
    const response = await this.api.get<ApiResponse>('/admin-dashboard/server-metrics/alert-settings');
    return response.data.data;
  }

  async updateServerAlertSettings(data: {
    alertsEnabled?: boolean;
    alertCpuPercent?: number;
    alertMemoryPercent?: number;
    alertDiskPercent?: number;
    alertLoadPerCore?: number;
    alertCooldownMinutes?: number;
  }): Promise<any> {
    const response = await this.api.put<ApiResponse>('/admin-dashboard/server-metrics/alert-settings', data);
    return response.data.data;
  }

  async getServerMetricsVapidKey(): Promise<string | null> {
    const response = await this.api.get<ApiResponse>('/admin-dashboard/server-metrics/vapid-key');
    return response.data.data?.publicKey || null;
  }

  async subscribeSuperAdminPush(subscription: any, userAgent?: string): Promise<any> {
    const response = await this.api.post<ApiResponse>('/admin-dashboard/server-metrics/push/subscribe', {
      subscription,
      userAgent,
    });
    return response.data;
  }

  async unsubscribeSuperAdminPush(endpoint: string): Promise<any> {
    const response = await this.api.post<ApiResponse>('/admin-dashboard/server-metrics/push/unsubscribe', {
      endpoint,
    });
    return response.data;
  }

  async getSuperAdminPushStatus(): Promise<{
    subscribed: boolean;
    count: number;
    vapidConfigured: boolean;
  }> {
    const response = await this.api.get<ApiResponse>('/admin-dashboard/server-metrics/push/status');
    return response.data.data;
  }

  // Client auth endpoints
  async findClientsForRegistration(phone?: string, email?: string): Promise<any> {
    const response = await this.api.post<ApiResponse>('/client-auth/find', { phone, email });
    return response.data.data;
  }

  async registerClient(clientId: string, tenantId: string, password: string): Promise<any> {
    const response = await this.api.post<ApiResponse>('/client-auth/register', {
      clientId,
      tenantId,
      password
    });
    return response.data;
  }

  async findParentsForRegistration(phone?: string, email?: string): Promise<any> {
    const response = await this.api.post<ApiResponse>('/client-auth/parent/find', { phone, email });
    return response.data.data;
  }

  async registerParent(parentId: string, tenantId: string, password: string): Promise<any> {
    const response = await this.api.post<ApiResponse>('/client-auth/parent/register', {
      parentId,
      tenantId,
      password
    });
    return response.data;
  }

  async loginClient(phone: string | undefined, email: string | undefined, password: string, tenantId: string): Promise<any> {
    const response = await this.api.post<ApiResponse>('/client-auth/login', {
      phone,
      email,
      password,
      tenantId
    });
    return response.data.data;
  }

  async getClientProfile(): Promise<any> {
    const response = await this.api.get<ApiResponse>('/client-auth/profile');
    return response.data.data;
  }

  /** Карточка спортсмена в ЛК (только свой / linked athlete). */
  async getAthleteCard(clientId?: string): Promise<any> {
    const response = await this.api.get<ApiResponse>('/client-auth/athlete-card', {
      params: clientId ? { clientId } : undefined,
    });
    return response.data.data;
  }

  /** Обновление карточки / аватара из ЛК клиента. */
  async updateAthleteCard(clientId: string, data: any): Promise<any> {
    const response = await this.api.put<ApiResponse>('/client-auth/athlete-card', {
      clientId,
      ...data,
    });
    return response.data.data;
  }

  /** Календарь ЛК: свои тренировки + соревнования, где клиент участник. */
  async getClientCalendarPlan(params?: {
    clientId?: string;
    from?: string;
    to?: string;
  }): Promise<{
    clientId: string;
    from: string;
    to: string;
    trainings: any[];
    competitions: any[];
  }> {
    const response = await this.api.get<ApiResponse>('/client-auth/calendar-plan', { params });
    return response.data.data!;
  }

  /** Платежи ЛК: только выставленные выбранному спортсмену. */
  async getClientPayments(clientId?: string): Promise<any[]> {
    const response = await this.api.get<ApiResponse>('/client-auth/payments', {
      params: clientId ? { clientId } : undefined,
    });
    return (response.data.data as any[]) || [];
  }

  async getPortalPaymentMethods(clientId?: string): Promise<any> {
    const response = await this.api.get<ApiResponse>('/client-auth/payment-methods', {
      params: clientId ? { clientId } : undefined,
    });
    return response.data.data;
  }

  async getPortalPaymentMethodQrBlob(id: string): Promise<Blob> {
    const response = await this.api.get(`/client-auth/payment-methods/${id}/qr`, {
      responseType: 'blob',
    });
    return response.data;
  }

  async submitPortalPaymentReceipt(
    paymentId: string,
    file: File,
    claimedAmount: number,
    clientId?: string
  ): Promise<any> {
    const form = new FormData();
    form.append('file', file);
    form.append('claimedAmount', String(claimedAmount));
    const response = await this.api.post<ApiResponse>(
      `/client-auth/payments/${paymentId}/receipt`,
      form,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        params: clientId ? { clientId } : undefined,
      }
    );
    return response.data.data;
  }

  /** Данные для панели управления клиента/родителя (учитывает подтверждение школой). */
  async getClientDashboard(clientId?: string): Promise<ClientDashboardData> {
    const response = await this.api.get<ApiResponse<ClientDashboardData>>('/client-auth/dashboard', {
      params: clientId ? { clientId } : undefined,
    });
    return response.data.data!;
  }

  async getPortalMembershipCatalog(): Promise<any[]> {
    const response = await this.api.get<ApiResponse>('/client-auth/membership-catalog');
    return response.data.data || [];
  }

  async changePortalMembership(data: { membershipId: string; clientId?: string }): Promise<any> {
    const response = await this.api.post<ApiResponse>('/client-auth/change-membership', data);
    return response.data.data;
  }

  /** Карточка тренера в ЛК клиента/родителя. */
  async getClientTrainerCard(trainerId: string, clientId?: string): Promise<ClientTrainerCardData> {
    const response = await this.api.get<ApiResponse<ClientTrainerCardData>>(
      `/client-auth/trainers/${trainerId}/card`,
      { params: clientId ? { clientId } : undefined }
    );
    return response.data.data!;
  }

  async approveClientAccount(clientId: string): Promise<any> {
    const response = await this.api.put<ApiResponse>(`/clients/${clientId}/approve-account`);
    return response.data.data;
  }

  async rejectClientAccount(clientId: string): Promise<any> {
    const response = await this.api.put<ApiResponse>(`/clients/${clientId}/reject-account`);
    return response.data.data;
  }

  async globalSearch(q: string): Promise<{
    clients: Array<{ id: string; type: 'client'; title: string; subtitle?: string }>;
    trainers: Array<{ id: string; type: 'trainer'; title: string; subtitle?: string }>;
    groups: Array<{ id: string; type: 'group'; title: string; subtitle?: string }>;
  }> {
    const response = await this.api.get<ApiResponse>('/search', { params: { q } });
    return response.data.data || { clients: [], trainers: [], groups: [] };
  }

  async getClientTrainings(startDate?: string, endDate?: string): Promise<any> {
    const params: any = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    const response = await this.api.get<ApiResponse>('/client-auth/trainings', { params });
    return response.data.data;
  }

  // Клиент: техподдержка (SUPPORT)
  async clientGetSupportConfig(): Promise<{
    quickSetupPromoEnabled: boolean;
    promos?: Array<{
      id: string;
      key: string | null;
      title: string;
      description: string;
      badgeLabel: string | null;
      buttonLabel: string;
      ticketKind: string;
      ticketSubject: string | null;
      defaultMessage: string | null;
      audience: string;
      accountScope?: string;
      newAccountDays?: number;
    }>;
    faq?: Array<{
      id: string;
      title: string;
      hint: string | null;
      topic: string | null;
      iconKey: string | null;
      sortOrder: number;
    }>;
  }> {
    const response = await this.api.get<ApiResponse>('/client-auth/support/config');
    return response.data.data;
  }

  async clientSupportUnreadSummary(): Promise<{ unreadTotal: number }> {
    const response = await this.api.get<ApiResponse>('/client-auth/support/unread-summary');
    return response.data.data;
  }

  async clientCreateSupportTicket(body: {
    channel?: 'SUPPORT';
    subject?: string;
    message: string;
    kind?: 'GENERAL' | 'QUICK_SETUP';
    promoId?: string;
    files?: File[];
  }): Promise<any> {
    if (body.files?.length) {
      const fd = new FormData();
      if (body.subject) fd.append('subject', body.subject);
      fd.append('message', body.message);
      fd.append('channel', 'SUPPORT');
      if (body.kind) fd.append('kind', body.kind);
      if (body.promoId) fd.append('promoId', body.promoId);
      body.files.forEach((f) => fd.append('files', f));
      const response = await this.api.post<ApiResponse>('/client-auth/support/tickets', fd);
      return response.data.data;
    }
    const response = await this.api.post<ApiResponse>('/client-auth/support/tickets', {
      channel: 'SUPPORT',
      subject: body.subject,
      message: body.message,
      kind: body.kind,
      promoId: body.promoId,
    });
    return response.data.data;
  }

  async clientListSupportTickets(): Promise<any> {
    const response = await this.api.get<ApiResponse>('/client-auth/support/tickets');
    return response.data.data;
  }

  async clientGetSupportTicketMessages(ticketId: string): Promise<any> {
    const response = await this.api.get<ApiResponse>(`/client-auth/support/tickets/${ticketId}/messages`);
    return response.data.data;
  }

  async clientPostSupportMessage(ticketId: string, message: string, files?: File[]): Promise<any> {
    if (files?.length) {
      const fd = new FormData();
      fd.append('message', message);
      files.forEach((f) => fd.append('files', f));
      const response = await this.api.post<ApiResponse>(`/client-auth/support/tickets/${ticketId}/messages`, fd);
      return response.data.data;
    }
    const response = await this.api.post<ApiResponse>(`/client-auth/support/tickets/${ticketId}/messages`, {
      message,
    });
    return response.data.data;
  }

  clientSupportAttachmentUrl(attachmentId: string): string {
    return `/api/client-auth/support/attachments/${attachmentId}`;
  }

  async clientDownloadSupportAttachmentBlob(
    attachmentId: string
  ): Promise<{ blob: Blob; filename: string; mimeType: string }> {
    const response = await this.api.get(`/client-auth/support/attachments/${attachmentId}`, {
      responseType: 'blob',
      timeout: 120000,
    });
    const mimeType =
      (response.headers['content-type'] as string) ||
      response.data?.type ||
      'application/octet-stream';
    const disposition = response.headers['content-disposition'] as string | undefined;
    const filename = parseContentDispositionFilename(disposition, `file-${attachmentId}`);
    return { blob: response.data, filename, mimeType };
  }

  // Non-client support (tenant users)
  async requesterGetSupportConfig(): Promise<{
    quickSetupPromoEnabled: boolean;
    promos?: Array<{
      id: string;
      key: string | null;
      title: string;
      description: string;
      badgeLabel: string | null;
      buttonLabel: string;
      ticketKind: string;
      ticketSubject: string | null;
      defaultMessage: string | null;
      audience: string;
      accountScope?: string;
      newAccountDays?: number;
    }>;
    faq?: Array<{
      id: string;
      title: string;
      hint: string | null;
      topic: string | null;
      iconKey: string | null;
      sortOrder: number;
    }>;
  }> {
    const response = await this.api.get<ApiResponse>('/support/config');
    return response.data.data;
  }

  async requesterSupportUnreadSummary(): Promise<{ unreadTotal: number }> {
    const response = await this.api.get<ApiResponse>('/support/unread-summary');
    return response.data.data;
  }

  async requesterCreateSupportTicket(body: {
    channel?: 'SUPPORT';
    subject?: string;
    message: string;
    kind?: 'GENERAL' | 'QUICK_SETUP';
    promoId?: string;
    files?: File[];
  }): Promise<any> {
    if (body.files?.length) {
      const fd = new FormData();
      if (body.subject) fd.append('subject', body.subject);
      fd.append('message', body.message);
      fd.append('channel', 'SUPPORT');
      if (body.kind) fd.append('kind', body.kind);
      if (body.promoId) fd.append('promoId', body.promoId);
      body.files.forEach((f) => fd.append('files', f));
      const response = await this.api.post<ApiResponse>('/support/tickets', fd);
      return response.data.data;
    }
    const response = await this.api.post<ApiResponse>('/support/tickets', {
      channel: 'SUPPORT',
      subject: body.subject,
      message: body.message,
      kind: body.kind,
      promoId: body.promoId,
    });
    return response.data.data;
  }

  async requesterListSupportTickets(params?: { channel?: 'SUPPORT' }): Promise<any> {
    const response = await this.api.get<ApiResponse>('/support/tickets', { params });
    return response.data.data;
  }

  async requesterGetSupportTicketMessages(ticketId: string): Promise<any> {
    const response = await this.api.get<ApiResponse>(`/support/tickets/${ticketId}/messages`);
    return response.data.data;
  }

  async requesterPostSupportMessage(ticketId: string, message: string, files?: File[]): Promise<any> {
    if (files?.length) {
      const fd = new FormData();
      fd.append('message', message);
      files.forEach((f) => fd.append('files', f));
      const response = await this.api.post<ApiResponse>(`/support/tickets/${ticketId}/messages`, fd);
      return response.data.data;
    }
    const response = await this.api.post<ApiResponse>(`/support/tickets/${ticketId}/messages`, { message });
    return response.data.data;
  }

  requesterSupportAttachmentUrl(attachmentId: string): string {
    return `/api/support/attachments/${attachmentId}`;
  }

  async requesterDownloadSupportAttachmentBlob(
    attachmentId: string
  ): Promise<{ blob: Blob; filename: string; mimeType: string }> {
    const response = await this.api.get(`/support/attachments/${attachmentId}`, {
      responseType: 'blob',
      timeout: 120000,
    });
    const mimeType =
      (response.headers['content-type'] as string) ||
      response.data?.type ||
      'application/octet-stream';
    const disposition = response.headers['content-disposition'] as string | undefined;
    const filename = parseContentDispositionFilename(disposition, `file-${attachmentId}`);
    return { blob: response.data, filename, mimeType };
  }

  async requesterUploadDesignerRecording(ticketId: string, formData: FormData): Promise<any> {
    const response = await this.api.post<ApiResponse>(`/support/tickets/${ticketId}/recordings`, formData);
    return response.data.data;
  }

  async platformStaffLogin(email: string, password: string): Promise<any> {
    const response = await this.api.post<ApiResponse>('/platform-staff/auth/login', { email, password });
    return response.data.data;
  }

  async platformStaffChangePassword(currentPassword: string, newPassword: string): Promise<void> {
    await this.api.post<ApiResponse>('/platform-staff/auth/change-password', { currentPassword, newPassword });
  }

  async platformStaffListTickets(params?: {
    status?: string;
    channel?: string;
    waitingReply?: boolean;
  }): Promise<any> {
    const response = await this.api.get<ApiResponse>('/platform-staff/tickets', {
      params: {
        ...params,
        waitingReply: params?.waitingReply ? '1' : undefined,
      },
    });
    return response.data.data;
  }

  async platformStaffClaimTicket(ticketId: string): Promise<any> {
    const response = await this.api.post<ApiResponse>(`/platform-staff/tickets/${ticketId}/claim`, {});
    return response.data.data;
  }

  async platformStaffCloseTicket(ticketId: string): Promise<any> {
    const response = await this.api.post<ApiResponse>(
      `/platform-staff/tickets/${ticketId}/close`,
      {}
    );
    return response.data.data;
  }

  async platformStaffGetMessages(ticketId: string): Promise<any> {
    const response = await this.api.get<ApiResponse>(`/platform-staff/tickets/${ticketId}/messages`);
    return response.data.data;
  }

  async platformStaffDownloadSupportAttachmentBlob(
    attachmentId: string
  ): Promise<{ blob: Blob; filename: string; mimeType: string }> {
    const response = await this.api.get(`/platform-staff/attachments/${attachmentId}`, {
      responseType: 'blob',
      timeout: 120000,
    });
    const mimeType =
      (response.headers['content-type'] as string) ||
      response.data?.type ||
      'application/octet-stream';
    const disposition = response.headers['content-disposition'] as string | undefined;
    const filename = parseContentDispositionFilename(disposition, `file-${attachmentId}`);
    return { blob: response.data, filename, mimeType };
  }

  async platformStaffPostMessage(ticketId: string, message: string): Promise<any> {
    const response = await this.api.post<ApiResponse>(`/platform-staff/tickets/${ticketId}/messages`, { message });
    return response.data.data;
  }

  async platformStaffListKnowledge(): Promise<any> {
    const response = await this.api.get<ApiResponse>('/platform-staff/knowledge');
    return response.data.data;
  }

  async platformStaffUploadRecording(formData: FormData): Promise<any> {
    const response = await this.api.post<ApiResponse>('/platform-staff/recordings', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data.data;
  }

  async superAdminListSupportTickets(params?: {
    channel?: string;
    tenantId?: string;
    status?: string;
    kind?: string;
    q?: string;
    waitingReply?: boolean;
  }): Promise<any> {
    const response = await this.api.get<ApiResponse>('/super-admin/support/tickets', {
      params: {
        ...params,
        waitingReply: params?.waitingReply ? '1' : undefined,
      },
    });
    return response.data.data;
  }

  async superAdminSupportUnreadTotal(): Promise<{
    unreadTotal: number;
    waitingReply: number;
    supportOnline: boolean;
  }> {
    const response = await this.api.get<ApiResponse>('/super-admin/support/tickets/unread-total');
    return response.data.data;
  }

  async superAdminGetSupportTicketDetail(ticketId: string): Promise<any> {
    const response = await this.api.get<ApiResponse>(`/super-admin/support/tickets/${ticketId}`);
    return response.data.data;
  }

  async superAdminCloseSupportTicket(ticketId: string): Promise<any> {
    const response = await this.api.post<ApiResponse>(
      `/super-admin/support/tickets/${ticketId}/close`,
      {}
    );
    return response.data.data;
  }

  async superAdminReopenSupportTicket(ticketId: string): Promise<any> {
    const response = await this.api.post<ApiResponse>(
      `/super-admin/support/tickets/${ticketId}/reopen`,
      {}
    );
    return response.data.data;
  }

  async superAdminPostSupportMessage(
    ticketId: string,
    message: string,
    files?: File[]
  ): Promise<any> {
    if (files?.length) {
      const fd = new FormData();
      fd.append('message', message);
      files.forEach((f) => fd.append('files', f));
      const response = await this.api.post<ApiResponse>(
        `/super-admin/support/tickets/${ticketId}/messages`,
        fd
      );
      return response.data.data;
    }
    const response = await this.api.post<ApiResponse>(
      `/super-admin/support/tickets/${ticketId}/messages`,
      { message }
    );
    return response.data.data;
  }

  superAdminSupportAttachmentUrl(attachmentId: string): string {
    return `/api/super-admin/support/attachments/${attachmentId}`;
  }

  async superAdminDownloadSupportAttachmentBlob(
    attachmentId: string
  ): Promise<{ blob: Blob; filename: string; mimeType: string }> {
    const response = await this.api.get(
      `/super-admin/support/attachments/${attachmentId}`,
      { responseType: 'blob', timeout: 120000 }
    );
    const mimeType =
      (response.headers['content-type'] as string) ||
      response.data?.type ||
      'application/octet-stream';
    const disposition = response.headers['content-disposition'] as string | undefined;
    const filename = parseContentDispositionFilename(disposition, `file-${attachmentId}`);
    return { blob: response.data, filename, mimeType };
  }

  async superAdminListDesignerRecordings(): Promise<any> {
    const response = await this.api.get<ApiResponse>('/super-admin/support/recordings');
    return response.data.data;
  }

  async superAdminDownloadDesignerRecording(
    id: string
  ): Promise<{ blob: Blob; filename: string }> {
    const response = await this.api.get(`/super-admin/support/recordings/${id}/download`, {
      responseType: 'blob',
      timeout: 120000,
    });
    const disposition = response.headers['content-disposition'] as string | undefined;
    const filename = parseContentDispositionFilename(disposition, `recording-${id}.webm`);
    return { blob: response.data, filename };
  }

  async superAdminDeleteDesignerRecording(id: string): Promise<void> {
    await this.api.delete(`/super-admin/support/recordings/${id}`);
  }

  async superAdminListSupportKnowledge(): Promise<any> {
    const response = await this.api.get<ApiResponse>('/super-admin/support/knowledge');
    return response.data.data;
  }

  async superAdminCreateSupportKnowledge(payload: {
    title: string;
    body: string;
    category?: string;
    sortOrder?: number;
    audience?: string;
    hint?: string | null;
    topic?: string | null;
    iconKey?: string | null;
    isActive?: boolean;
  }): Promise<any> {
    const response = await this.api.post<ApiResponse>('/super-admin/support/knowledge', payload);
    return response.data.data;
  }

  async superAdminUpdateSupportKnowledge(
    id: string,
    payload: Partial<{
      title: string;
      body: string;
      category: string | null;
      sortOrder: number;
      audience: string;
      hint: string | null;
      topic: string | null;
      iconKey: string | null;
      isActive: boolean;
    }>
  ): Promise<any> {
    const response = await this.api.put<ApiResponse>(`/super-admin/support/knowledge/${id}`, payload);
    return response.data.data;
  }

  async superAdminDeleteSupportKnowledge(id: string): Promise<void> {
    await this.api.delete(`/super-admin/support/knowledge/${id}`);
  }

  async superAdminListCannedReplies(): Promise<{ replies: any[] }> {
    const response = await this.api.get<ApiResponse>('/super-admin/support/canned-replies');
    return response.data.data;
  }

  async superAdminCreateCannedReply(payload: {
    title: string;
    body: string;
    sortOrder?: number;
    isActive?: boolean;
  }): Promise<any> {
    const response = await this.api.post<ApiResponse>('/super-admin/support/canned-replies', payload);
    return response.data.data;
  }

  async superAdminUpdateCannedReply(
    id: string,
    payload: Partial<{ title: string; body: string; sortOrder: number; isActive: boolean }>
  ): Promise<any> {
    const response = await this.api.put<ApiResponse>(
      `/super-admin/support/canned-replies/${id}`,
      payload
    );
    return response.data.data;
  }

  async superAdminDeleteCannedReply(id: string): Promise<void> {
    await this.api.delete(`/super-admin/support/canned-replies/${id}`);
  }

  async superAdminListSupportPromos(): Promise<{ promos: any[] }> {
    const response = await this.api.get<ApiResponse>('/super-admin/support/promos');
    return response.data.data;
  }

  async superAdminCreateSupportPromo(payload: {
    title: string;
    description: string;
    buttonLabel: string;
    badgeLabel?: string | null;
    ticketKind?: 'GENERAL' | 'QUICK_SETUP';
    ticketSubject?: string | null;
    defaultMessage?: string | null;
    audience?: 'school' | 'marketer' | 'client' | 'all';
    accountScope?: 'ALL' | 'NEW';
    newAccountDays?: number;
    autoReplyEnabled?: boolean;
    autoReplyBody?: string | null;
    isActive?: boolean;
    sortOrder?: number;
  }): Promise<any> {
    const response = await this.api.post<ApiResponse>('/super-admin/support/promos', payload);
    return response.data.data;
  }

  async superAdminUpdateSupportPromo(
    id: string,
    payload: Partial<{
      title: string;
      description: string;
      buttonLabel: string;
      badgeLabel: string | null;
      ticketKind: 'GENERAL' | 'QUICK_SETUP';
      ticketSubject: string | null;
      defaultMessage: string | null;
      audience: 'school' | 'marketer' | 'client' | 'all';
      accountScope: 'ALL' | 'NEW';
      newAccountDays: number;
      autoReplyEnabled: boolean;
      autoReplyBody: string | null;
      isActive: boolean;
      sortOrder: number;
    }>
  ): Promise<any> {
    const response = await this.api.put<ApiResponse>(`/super-admin/support/promos/${id}`, payload);
    return response.data.data;
  }

  async superAdminDeleteSupportPromo(id: string): Promise<void> {
    await this.api.delete(`/super-admin/support/promos/${id}`);
  }

  async superAdminReorderSupportPromos(ids: string[]): Promise<{ promos: any[] }> {
    const response = await this.api.put<ApiResponse>('/super-admin/support/promos/reorder', { ids });
    return response.data.data;
  }

  async superAdminGetSupportAutoReplySettings(): Promise<{
    supportAutoReplyEnabled: boolean;
    supportAutoReplyBody: string | null;
    supportOnlineMinutes: number;
    attachments?: Array<{
      id: string;
      originalName: string;
      mimeType: string;
      size: number;
      createdAt: string;
    }>;
  }> {
    const response = await this.api.get<ApiResponse>('/super-admin/support/auto-reply-settings');
    return response.data.data;
  }

  async superAdminUpdateSupportAutoReplySettings(payload: {
    supportAutoReplyEnabled?: boolean;
    supportAutoReplyBody?: string | null;
    supportOnlineMinutes?: number;
  }): Promise<{
    supportAutoReplyEnabled: boolean;
    supportAutoReplyBody: string | null;
    supportOnlineMinutes: number;
    attachments?: Array<{
      id: string;
      originalName: string;
      mimeType: string;
      size: number;
      createdAt: string;
    }>;
  }> {
    const response = await this.api.put<ApiResponse>(
      '/super-admin/support/auto-reply-settings',
      payload
    );
    return response.data.data;
  }

  async superAdminUploadAutoReplyAttachment(
    file: File,
    promoId?: string | null
  ): Promise<{
    attachment: {
      id: string;
      originalName: string;
      mimeType: string;
      size: number;
      createdAt: string;
      promoId: string | null;
    };
  }> {
    const form = new FormData();
    form.append('file', file);
    if (promoId) form.append('promoId', promoId);
    const response = await this.api.post<ApiResponse>(
      '/super-admin/support/auto-reply-attachments',
      form
    );
    return response.data.data;
  }

  async superAdminDeleteAutoReplyAttachment(id: string): Promise<void> {
    await this.api.delete(`/super-admin/support/auto-reply-attachments/${id}`);
  }

  async superAdminDownloadAutoReplyAttachmentBlob(
    id: string
  ): Promise<{ blob: Blob; filename: string }> {
    const response = await this.api.get(`/super-admin/support/auto-reply-attachments/${id}`, {
      responseType: 'blob',
      timeout: 120000,
    });
    const disposition = response.headers['content-disposition'] as string | undefined;
    const filename = parseContentDispositionFilename(disposition, `file-${id}`);
    return { blob: response.data, filename };
  }

  async superAdminListPlatformStaffUsers(): Promise<any[]> {
    const response = await this.api.get<ApiResponse>('/super-admin/support/platform-staff/users');
    return response.data.data || [];
  }

  async superAdminCreatePlatformStaffUser(payload: { email: string; role: 'SUPPORT' | 'DESIGNER' | 'SECURITY'; firstName: string; lastName: string }): Promise<any> {
    const response = await this.api.post<ApiResponse>('/super-admin/support/platform-staff/users', payload);
    return response.data.data;
  }

  /* ------------------------------------------------------------------ */
  /* Внутришкольные чаты                                                */
  /* ------------------------------------------------------------------ */

  async listChatThreads(): Promise<any[]> {
    const response = await this.api.get<ApiResponse>('/chats/threads');
    return response.data.data || [];
  }

  async getChatUnreadTotal(): Promise<{ total: number }> {
    const response = await this.api.get<ApiResponse>('/chats/unread-total');
    return response.data.data || { total: 0 };
  }

  async ensureChatThread(data: {
    type: string;
    clientId?: string;
    trainerId?: string;
    groupId?: string;
  }): Promise<any> {
    const response = await this.api.post<ApiResponse>('/chats/threads/ensure', data);
    return response.data.data;
  }

  async getChatMessages(threadId: string, params?: { before?: string; limit?: number }): Promise<any> {
    const response = await this.api.get<ApiResponse>(`/chats/threads/${threadId}/messages`, { params });
    return response.data.data;
  }

  async sendChatMessage(
    threadId: string,
    bodyOrOpts:
      | string
      | {
          body: string;
          replyToMessageId?: string | null;
          mentions?: Array<{ type: string; id: string }>;
        }
  ): Promise<any> {
    const payload =
      typeof bodyOrOpts === 'string'
        ? { body: bodyOrOpts }
        : {
            body: bodyOrOpts.body,
            replyToMessageId: bodyOrOpts.replyToMessageId || undefined,
            mentions: bodyOrOpts.mentions,
          };
    const response = await this.api.post<ApiResponse>(`/chats/threads/${threadId}/messages`, payload);
    return response.data.data;
  }

  async sendChatMedia(
    threadId: string,
    data: {
      file: File;
      kind?: 'image' | 'video' | 'voice';
      caption?: string;
      replyToMessageId?: string | null;
      mentions?: Array<{ type: string; id: string }>;
      durationMs?: number | null;
    }
  ): Promise<any> {
    const form = new FormData();
    form.append('file', data.file);
    if (data.kind) form.append('kind', data.kind);
    if (data.caption) form.append('caption', data.caption);
    if (data.replyToMessageId) form.append('replyToMessageId', data.replyToMessageId);
    if (data.mentions?.length) form.append('mentions', JSON.stringify(data.mentions));
    if (data.durationMs != null) form.append('durationMs', String(data.durationMs));
    const response = await this.api.post<ApiResponse>(
      `/chats/threads/${threadId}/messages/media`,
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data.data;
  }

  async getChatMediaBlob(
    threadId: string,
    messageId: string,
    attachmentId: string
  ): Promise<Blob> {
    const response = await this.api.get(
      `/chats/threads/${threadId}/messages/${messageId}/media/${attachmentId}`,
      { responseType: 'blob' }
    );
    return response.data;
  }

  async getChatParticipants(threadId: string): Promise<any[]> {
    const response = await this.api.get<ApiResponse>(`/chats/threads/${threadId}/participants`);
    return response.data.data || [];
  }

  async pinChatMessage(threadId: string, messageId: string): Promise<any> {
    const response = await this.api.post<ApiResponse>(`/chats/threads/${threadId}/pins`, {
      messageId,
    });
    return response.data.data;
  }

  async unpinChatMessage(threadId: string, messageId: string): Promise<any> {
    const response = await this.api.delete<ApiResponse>(
      `/chats/threads/${threadId}/pins/${messageId}`
    );
    return response.data.data;
  }

  async editChatMessage(threadId: string, messageId: string, body: string): Promise<any> {
    const response = await this.api.patch<ApiResponse>(
      `/chats/threads/${threadId}/messages/${messageId}`,
      { body }
    );
    return response.data.data;
  }

  async deleteChatMessage(threadId: string, messageId: string): Promise<any> {
    const response = await this.api.delete<ApiResponse>(
      `/chats/threads/${threadId}/messages/${messageId}`
    );
    return response.data.data;
  }

  async markChatRead(threadId: string): Promise<any> {
    const response = await this.api.post<ApiResponse>(`/chats/threads/${threadId}/read`, {});
    return response.data.data;
  }

  async clientListChatThreads(): Promise<any[]> {
    const response = await this.api.get<ApiResponse>('/client-auth/chats/threads');
    return response.data.data || [];
  }

  async clientGetChatUnreadTotal(): Promise<{ total: number }> {
    const response = await this.api.get<ApiResponse>('/client-auth/chats/unread-total');
    return response.data.data || { total: 0 };
  }

  async clientEnsureChatThread(data: {
    type: string;
    clientId?: string;
    trainerId?: string;
    groupId?: string;
  }): Promise<any> {
    const response = await this.api.post<ApiResponse>('/client-auth/chats/threads/ensure', data);
    return response.data.data;
  }

  async clientGetChatMessages(threadId: string, params?: { before?: string; limit?: number }): Promise<any> {
    const response = await this.api.get<ApiResponse>(`/client-auth/chats/threads/${threadId}/messages`, {
      params,
    });
    return response.data.data;
  }

  async clientSendChatMessage(
    threadId: string,
    bodyOrOpts:
      | string
      | {
          body: string;
          replyToMessageId?: string | null;
          mentions?: Array<{ type: string; id: string }>;
        }
  ): Promise<any> {
    const payload =
      typeof bodyOrOpts === 'string'
        ? { body: bodyOrOpts }
        : {
            body: bodyOrOpts.body,
            replyToMessageId: bodyOrOpts.replyToMessageId || undefined,
            mentions: bodyOrOpts.mentions,
          };
    const response = await this.api.post<ApiResponse>(
      `/client-auth/chats/threads/${threadId}/messages`,
      payload
    );
    return response.data.data;
  }

  async clientSendChatMedia(
    threadId: string,
    data: {
      file: File;
      kind?: 'image' | 'video' | 'voice';
      caption?: string;
      replyToMessageId?: string | null;
      mentions?: Array<{ type: string; id: string }>;
      durationMs?: number | null;
    }
  ): Promise<any> {
    const form = new FormData();
    form.append('file', data.file);
    if (data.kind) form.append('kind', data.kind);
    if (data.caption) form.append('caption', data.caption);
    if (data.replyToMessageId) form.append('replyToMessageId', data.replyToMessageId);
    if (data.mentions?.length) form.append('mentions', JSON.stringify(data.mentions));
    if (data.durationMs != null) form.append('durationMs', String(data.durationMs));
    const response = await this.api.post<ApiResponse>(
      `/client-auth/chats/threads/${threadId}/messages/media`,
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data.data;
  }

  async clientGetChatMediaBlob(
    threadId: string,
    messageId: string,
    attachmentId: string
  ): Promise<Blob> {
    const response = await this.api.get(
      `/client-auth/chats/threads/${threadId}/messages/${messageId}/media/${attachmentId}`,
      { responseType: 'blob' }
    );
    return response.data;
  }

  async clientGetChatParticipants(threadId: string): Promise<any[]> {
    const response = await this.api.get<ApiResponse>(
      `/client-auth/chats/threads/${threadId}/participants`
    );
    return response.data.data || [];
  }

  async clientPinChatMessage(threadId: string, messageId: string): Promise<any> {
    const response = await this.api.post<ApiResponse>(
      `/client-auth/chats/threads/${threadId}/pins`,
      { messageId }
    );
    return response.data.data;
  }

  async clientUnpinChatMessage(threadId: string, messageId: string): Promise<any> {
    const response = await this.api.delete<ApiResponse>(
      `/client-auth/chats/threads/${threadId}/pins/${messageId}`
    );
    return response.data.data;
  }

  async clientEditChatMessage(threadId: string, messageId: string, body: string): Promise<any> {
    const response = await this.api.patch<ApiResponse>(
      `/client-auth/chats/threads/${threadId}/messages/${messageId}`,
      { body }
    );
    return response.data.data;
  }

  async clientDeleteChatMessage(threadId: string, messageId: string): Promise<any> {
    const response = await this.api.delete<ApiResponse>(
      `/client-auth/chats/threads/${threadId}/messages/${messageId}`
    );
    return response.data.data;
  }

  async clientMarkChatRead(threadId: string): Promise<any> {
    const response = await this.api.post<ApiResponse>(`/client-auth/chats/threads/${threadId}/read`, {});
    return response.data.data;
  }

  /* ------------------------------------------------------------------ */
  /* Платформенные чаты и changelog                                     */
  /* ------------------------------------------------------------------ */

  async listPlatformChatThreads(): Promise<any[]> {
    const response = await this.api.get<ApiResponse>('/platform/chats/threads');
    return response.data.data || [];
  }

  async ensurePlatformChatThread(data: { type: string }): Promise<any> {
    const response = await this.api.post<ApiResponse>('/platform/chats/threads/ensure', data);
    return response.data.data;
  }

  async getPlatformChatMessages(threadId: string, params?: { before?: string; limit?: number }): Promise<any> {
    const response = await this.api.get<ApiResponse>(`/platform/chats/threads/${threadId}/messages`, {
      params,
    });
    return response.data.data;
  }

  async sendPlatformChatMessage(
    threadId: string,
    bodyOrOpts:
      | string
      | {
          body: string;
          replyToMessageId?: string | null;
          mentions?: Array<{ type: string; id: string }>;
        }
  ): Promise<any> {
    const payload =
      typeof bodyOrOpts === 'string'
        ? { body: bodyOrOpts }
        : {
            body: bodyOrOpts.body,
            replyToMessageId: bodyOrOpts.replyToMessageId || undefined,
            mentions: bodyOrOpts.mentions,
          };
    const response = await this.api.post<ApiResponse>(
      `/platform/chats/threads/${threadId}/messages`,
      payload
    );
    return response.data.data;
  }

  async sendPlatformChatMedia(
    threadId: string,
    data: {
      file: File;
      kind?: 'image' | 'video' | 'voice';
      caption?: string;
      replyToMessageId?: string | null;
      mentions?: Array<{ type: string; id: string }>;
      durationMs?: number | null;
    }
  ): Promise<any> {
    const form = new FormData();
    form.append('file', data.file);
    if (data.kind) form.append('kind', data.kind);
    if (data.caption) form.append('caption', data.caption);
    if (data.replyToMessageId) form.append('replyToMessageId', data.replyToMessageId);
    if (data.mentions?.length) form.append('mentions', JSON.stringify(data.mentions));
    if (data.durationMs != null) form.append('durationMs', String(data.durationMs));
    const response = await this.api.post<ApiResponse>(
      `/platform/chats/threads/${threadId}/messages/media`,
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data.data;
  }

  async getPlatformChatMediaBlob(
    threadId: string,
    messageId: string,
    attachmentId: string
  ): Promise<Blob> {
    const response = await this.api.get(
      `/platform/chats/threads/${threadId}/messages/${messageId}/media/${attachmentId}`,
      { responseType: 'blob' }
    );
    return response.data;
  }

  async getPlatformChatParticipants(threadId: string): Promise<any[]> {
    const response = await this.api.get<ApiResponse>(
      `/platform/chats/threads/${threadId}/participants`
    );
    return response.data.data || [];
  }

  async pinPlatformChatMessage(threadId: string, messageId: string): Promise<any> {
    const response = await this.api.post<ApiResponse>(`/platform/chats/threads/${threadId}/pins`, {
      messageId,
    });
    return response.data.data;
  }

  async unpinPlatformChatMessage(threadId: string, messageId: string): Promise<any> {
    const response = await this.api.delete<ApiResponse>(
      `/platform/chats/threads/${threadId}/pins/${messageId}`
    );
    return response.data.data;
  }

  async editPlatformChatMessage(threadId: string, messageId: string, body: string): Promise<any> {
    const response = await this.api.patch<ApiResponse>(
      `/platform/chats/threads/${threadId}/messages/${messageId}`,
      { body }
    );
    return response.data.data;
  }

  async deletePlatformChatMessage(threadId: string, messageId: string): Promise<any> {
    const response = await this.api.delete<ApiResponse>(
      `/platform/chats/threads/${threadId}/messages/${messageId}`
    );
    return response.data.data;
  }

  async markPlatformChatRead(threadId: string): Promise<any> {
    const response = await this.api.post<ApiResponse>(`/platform/chats/threads/${threadId}/read`, {});
    return response.data.data;
  }

  async listPlatformChangelog(): Promise<any[]> {
    const response = await this.api.get<ApiResponse>('/platform/changelog');
    return response.data.data || [];
  }

  async createPlatformChangelog(data: {
    title: string;
    body: string;
    audienceRoles?: string[];
    tag?: string;
    /** ISO; будущее = отложенная публикация */
    publishedAt?: string;
    image?: File | null;
    file?: File | null;
  }): Promise<any> {
    const hasFiles = Boolean(data.image || data.file);
    if (!hasFiles) {
      const response = await this.api.post<ApiResponse>('/platform/changelog', {
        title: data.title,
        body: data.body,
        audienceRoles: data.audienceRoles || [],
        tag: data.tag,
        publishedAt: data.publishedAt,
      });
      return response.data.data;
    }
    const form = new FormData();
    form.append('title', data.title);
    form.append('body', data.body);
    // Всегда передаём массив, в т.ч. [] — «только супер-админы»
    form.append('audienceRoles', JSON.stringify(data.audienceRoles || []));
    if (data.tag) form.append('tag', data.tag);
    if (data.publishedAt) form.append('publishedAt', data.publishedAt);
    if (data.image) form.append('image', data.image);
    if (data.file) form.append('file', data.file);
    const response = await this.api.post<ApiResponse>('/platform/changelog', form, {
      timeout: 60000,
    });
    return response.data.data;
  }

  async updatePlatformChangelog(
    id: string,
    data: {
      title?: string;
      body?: string;
      audienceRoles?: string[];
      tag?: string;
      publishedAt?: string;
      image?: File | null;
      file?: File | null;
      clearImage?: boolean;
      clearFile?: boolean;
    }
  ): Promise<any> {
    const hasFiles = Boolean(data.image || data.file);
    if (!hasFiles) {
      const response = await this.api.put<ApiResponse>(`/platform/changelog/${id}`, {
        title: data.title,
        body: data.body,
        audienceRoles: data.audienceRoles,
        tag: data.tag,
        publishedAt: data.publishedAt,
        clearImage: data.clearImage || undefined,
        clearFile: data.clearFile || undefined,
      });
      return response.data.data;
    }
    const form = new FormData();
    if (data.title !== undefined) form.append('title', data.title);
    if (data.body !== undefined) form.append('body', data.body);
    if (data.audienceRoles !== undefined) {
      form.append('audienceRoles', JSON.stringify(data.audienceRoles));
    }
    if (data.tag !== undefined) form.append('tag', data.tag);
    if (data.publishedAt !== undefined) form.append('publishedAt', data.publishedAt);
    if (data.clearImage) form.append('clearImage', 'true');
    if (data.clearFile) form.append('clearFile', 'true');
    if (data.image) form.append('image', data.image);
    if (data.file) form.append('file', data.file);
    const response = await this.api.put<ApiResponse>(`/platform/changelog/${id}`, form, {
      timeout: 60000,
    });
    return response.data.data;
  }

  async deletePlatformChangelog(id: string): Promise<any> {
    const response = await this.api.delete<ApiResponse>(`/platform/changelog/${id}`);
    return response.data.data;
  }

  /** Лента новостей для школы (фильтр по роли JWT). */
  async listPlatformNews(): Promise<any[]> {
    const response = await this.api.get<ApiResponse>('/platform/news');
    return response.data.data || [];
  }

  async downloadPlatformNewsImageBlob(id: string, source: 'school' | 'sa' | 'portal' | 'marketer' = 'school'): Promise<Blob> {
    const path =
      source === 'sa'
        ? `/platform/changelog/${id}/image`
        : source === 'portal'
          ? `/client-auth/news/${id}/image`
          : source === 'marketer'
            ? `/marketers/me/news/${id}/image`
            : `/platform/news/${id}/image`;
    const response = await this.api.get(path, { responseType: 'blob', timeout: 60000 });
    const blob = response.data as Blob;
    if (blob?.type?.includes('application/json')) {
      const text = await blob.text();
      let message = 'Не удалось загрузить изображение';
      try {
        const parsed = JSON.parse(text);
        if (parsed?.error) message = String(parsed.error);
      } catch {
        /* ignore */
      }
      throw new Error(message);
    }
    return blob;
  }

  async downloadPlatformNewsFileBlob(id: string, source: 'school' | 'sa' | 'portal' | 'marketer' = 'school'): Promise<Blob> {
    const path =
      source === 'sa'
        ? `/platform/changelog/${id}/file`
        : source === 'portal'
          ? `/client-auth/news/${id}/file`
          : source === 'marketer'
            ? `/marketers/me/news/${id}/file`
            : `/platform/news/${id}/file`;
    const response = await this.api.get(path, { responseType: 'blob', timeout: 60000 });
    const blob = response.data as Blob;
    if (blob?.type?.includes('application/json')) {
      const text = await blob.text();
      let message = 'Не удалось скачать файл';
      try {
        const parsed = JSON.parse(text);
        if (parsed?.error) message = String(parsed.error);
      } catch {
        /* ignore */
      }
      throw new Error(message);
    }
    return blob;
  }

  async listPortalNews(): Promise<any[]> {
    const response = await this.api.get<ApiResponse>('/client-auth/news');
    return response.data.data || [];
  }

  async listMarketerPlatformNews(): Promise<any[]> {
    const response = await this.api.get<ApiResponse>('/marketers/me/news');
    return response.data.data || [];
  }

  /* ------------------------------------------------------------------ */
  /* Идеи для разработчика                                              */
  /* ------------------------------------------------------------------ */

  private featureIdeasBase(source: 'school' | 'portal' | 'marketer' = 'school'): string {
    if (source === 'portal') return '/client-auth/feature-ideas';
    if (source === 'marketer') return '/marketers/me/feature-ideas';
    return '/feature-ideas';
  }

  async listMyFeatureIdeas(source: 'school' | 'portal' | 'marketer' = 'school'): Promise<any[]> {
    const response = await this.api.get<ApiResponse>(this.featureIdeasBase(source));
    return response.data.data || [];
  }

  async createFeatureIdea(
    data: { title: string; body: string },
    source: 'school' | 'portal' | 'marketer' = 'school'
  ): Promise<any> {
    const response = await this.api.post<ApiResponse>(this.featureIdeasBase(source), data);
    return response.data.data;
  }

  async adminListFeatureIdeas(params?: { status?: string }): Promise<any[]> {
    const response = await this.api.get<ApiResponse>('/admin-dashboard/feature-ideas', { params });
    return response.data.data || [];
  }

  async adminAcceptFeatureIdea(id: string): Promise<any> {
    const response = await this.api.post<ApiResponse>(`/admin-dashboard/feature-ideas/${id}/accept`, {});
    return response.data.data;
  }

  async adminRejectFeatureIdea(id: string, rejectReason?: string): Promise<any> {
    const response = await this.api.post<ApiResponse>(`/admin-dashboard/feature-ideas/${id}/reject`, {
      rejectReason: rejectReason || undefined,
    });
    return response.data.data;
  }
}

export const apiService = new ApiService();
